/*
 * Mitgelieferte Board-Vorlagen (0.29): Kategorien, Kurzbeschreibung für die Kachel, Erklärung für die
 * Detailseite, Merkmale und Beispielkarten für die Vorschau. Platzhalter in Automationen und Karten:
 * „col:<index>“, „label:<index>“, „field:<index>“ (siehe templates.ts).
 */
import type { BoardTemplateData } from './templates.js';
import { DEFAULT_APPROVAL } from './approval.js';

export const TEMPLATE_CATEGORIES = [
  ['projekte', 'Projekte & Teamarbeit'],
  ['veranstaltungen', 'Veranstaltungen'],
  ['bildung', 'Bildung & Schule'],
  ['marketing', 'Marketing & Kommunikation'],
  ['design', 'Design & Medien'],
  ['verein', 'Verein & Ehrenamt'],
  ['finanzen', 'Finanzen & Verwaltung'],
  ['personal', 'Personal & Organisation'],
  ['it', 'Software & IT'],
  ['service', 'Anfragen & Service'],
  ['vertrieb', 'Vertrieb & Kunden'],
  ['persoenlich', 'Persönlich & Alltag'],
] as const;
export type TemplateCategory = (typeof TEMPLATE_CATEGORIES)[number][0];
export const TEMPLATE_CATEGORY_LABELS: Record<TemplateCategory | 'eigene', string> = {
  ...(Object.fromEntries(TEMPLATE_CATEGORIES) as Record<TemplateCategory, string>),
  eigene: 'Eigene Vorlagen',
};

export interface BuiltinTemplate {
  id: string;
  name: string;
  category: TemplateCategory;
  /** Eine Zeile für die Kachel */
  summary: string;
  /** Zwei, drei Sätze für die Detailseite */
  description: string;
  /** Merkmale als Chips, z. B. „WIP-Limit“, „Checklisten“ */
  tags: string[];
  /** Akzentfarbe der Vorschau (HEX) */
  color: string;
  /** Beispielkarten standardmäßig mit anlegen (Checklisten-Vorlagen wie Umzug, Onboarding) */
  cardsDefault?: boolean;
  /** 0.86: Werte, die in der Vorlage stecken und vor dem Start angepasst werden sollten */
  adjust?: string[];
  /** 0.86: im Katalog als empfohlener Einstieg hervorheben */
  recommended?: boolean;
  /**
   * 0.67: Fassung der Vorlage (ohne Angabe 1). Bei inhaltlichen Änderungen erhöhen – bestehende Boards merken
   * sich Kennung und Fassung und werden nie nachträglich verändert.
   */
  version?: number;
  data: BoardTemplateData;
}

import { c, cols, opts, fieldBecomes, view, labelsOf, wg, ROWS, done, meOn } from './templateHelpers.js';
import { FINANCE_TEMPLATES, TAX_RETURN_TEMPLATE } from './templateCatalogFinanz.js';

export const TEMPLATE_CATALOG: BuiltinTemplate[] = [
  /* ---------- Projekte & Teamarbeit ---------- */
  {
    id: 'builtin:simple',
    recommended: true,
    name: 'Einfaches Kanban',
    category: 'projekte',
    summary: 'Offen, In Arbeit, Prüfung, Erledigt – der bewährte Start',
    description:
      'Das klassische Kanban-Board für jede Art von Aufgaben. Vier Spalten zeigen auf einen Blick, was ansteht, was läuft und was fertig ist. Wer eine Karte nach „Erledigt“ zieht, hakt sie automatisch ab.',
    tags: ['4 Spalten', 'Automatisch erledigt'],
    color: '#3b5bdb',
    data: {
      columns: cols('Offen', 'In Arbeit', 'Prüfung', 'Erledigt'),
      labels: [
        { name: 'Wichtig', color: 'red' },
        { name: 'Schnell erledigt', color: 'green' },
        { name: 'Wartet', color: 'yellow' },
        { name: 'Idee', color: 'purple' },
      ],
      automations: [{ name: 'Erledigt-Spalte hakt ab', trigger: { type: 'moved', columnId: 'col:3' }, actions: [{ type: 'complete' }] }],
      cards: [
        c(0, 'Angebot für den Druck einholen', [0], { dueInDays: 5 }),
        c(0, 'Termin mit dem Vorstand finden', [2]),
        c(0, 'Ideen für das Sommerfest sammeln', [3]),
        c(1, 'Website-Texte überarbeiten', [], { checklist: ['Startseite', 'Über uns', 'Kontakt'] }),
        c(1, 'Fotos sortieren', [1]),
        c(2, 'Flyer-Entwurf', [], { checklist: ['Text', 'Bilder', 'Freigabe'] }),
        c(3, 'Raum reserviert', [1]),
      ],
    },
  },
  {
    id: 'builtin:project',
    version: 5,
    name: 'Projekt mit Prüfung',
    category: 'projekte',
    summary: 'Ideen bis Abschluss, WIP-Limit, Freigabe nach dem Vier-Augen-Prinzip',
    description:
      'Für Projekte, bei denen Qualität zählt: Eine Begrenzung für „In Arbeit“ verhindert Verzetteln, Spaltenregeln sagen, wann eine Aufgabe weiter darf. Ist die Checkliste fertig, ist die Karte prüfbereit und wandert in die Prüfung. Abgeschlossen ist sie erst mit einer Freigabe – und die gibt jemand, der nicht selbst zuständig war; eine Ablehnung schickt die Karte zurück in Arbeit. Für Meilensteine, Risiken und Zulieferungen gibt es eigene Kartenvorlagen; Abhängigkeiten verbinden, was aufeinander wartet.',
    tags: ['WIP-Limit', 'Vier-Augen-Freigabe', 'Meilensteine & Risiken', '5 Automationen'],
    color: '#5f3dc4',
    data: {
      views: [
        view('Meine Aufgaben', { filter: { assignees: ['me'], completed: 'hide' }, sort: 'due' }),
        view('Meilensteine und Risiken', { view: 'list', filter: { labels: labelsOf(1, 2) }, sort: 'due' }),
        view('Zeitplan', { view: 'timeline' }),
      ],
      overview: { widgets: [wg('deadlines', 12), wg('blocked', 6, ROWS), wg('ready', 6, ROWS), wg('tasks', 6, { rows: 10, sort: 'due' }, 'Ohne Zuständige', { assignees: ['none'] }), wg('flow', 6)] },
      columns: cols(
        'Ideen',
        { title: 'Geplant', entryRule: 'Ziel und Ansprechperson stehen fest' },
        { title: 'In Arbeit', wipLimit: 5 },
        { title: 'Prüfung', exitRule: 'Freigegeben oder zurück zur Überarbeitung' },
        {
          title: 'Erledigt',
          color: 'green',
          entryGate: {
            when: { op: 'all', items: [{ type: 'field', fieldId: 'field:0', test: { op: 'approved' } }] },
            message: 'Erledigt ist eine Aufgabe erst mit Freigabe durch eine zweite Person.',
            ownerOverride: true,
          },
        },
      ),
      labels: [
        { name: 'Wichtig', color: 'red' },
        { name: 'Meilenstein', color: 'blue' },
        { name: 'Risiko', color: 'orange' },
        { name: 'Wartet', color: 'yellow' },
        { name: 'Idee', color: 'purple' },
      ],
      fields: [{ name: 'Freigabe', pinned: true, type: 'approval', showOnCard: true, config: { approval: { ...DEFAULT_APPROVAL, notCreator: false, notAssignee: true } } }],
      cardTemplates: [
        {
          name: 'Aufgabe',
          title: 'Neue Aufgabe',
          description: '<p><strong>Ziel:</strong> </p><p><strong>Fertig, wenn:</strong> </p>',
          checklist: ['Ziel klären', 'Umsetzen', 'Prüfen lassen'],
          assignCreator: true,
        },
        {
          name: 'Meilenstein',
          title: 'Meilenstein: ',
          labelIds: ['label:1'],
          description: '<p><strong>Erreicht, wenn:</strong> </p><p><strong>Datum:</strong> steht als Frist auf der Karte.</p><p>Aufgaben, die vorher fertig sein müssen, unter „Abhängigkeiten“ verknüpfen.</p>',
        },
        {
          name: 'Risiko',
          title: 'Risiko: ',
          labelIds: ['label:2'],
          description: '<p><strong>Was kann passieren?</strong> </p><p><strong>Auswirkung:</strong> </p><p><strong>Gegenmaßnahme:</strong> </p><p><strong>Spätestens prüfen bis:</strong> Frist der Karte.</p>',
        },
        {
          name: 'Zulieferung',
          title: 'Zulieferung: ',
          labelIds: ['label:3'],
          description: '<p><strong>Wer liefert was?</strong> </p><p>Karten, die darauf warten, verknüpfst du unter „Abhängigkeiten“ mit dieser Karte.</p>',
        },
      ],
      automations: [
        meOn(2),
        { name: 'Checkliste fertig → prüfbereit', trigger: { type: 'checklistDone' }, actions: [{ type: 'move', columnId: 'col:3', placement: 'bottom' }] },
        { name: 'Freigegeben → Erledigt', trigger: { type: 'fieldChanged', fieldId: 'field:0', to: { op: 'approved' } }, actions: [{ type: 'move', columnId: 'col:4', placement: 'top' }, { type: 'complete' }] },
        { name: 'Abgelehnt → zurück in Arbeit', trigger: { type: 'fieldChanged', fieldId: 'field:0', to: { op: 'rejected' } }, actions: [{ type: 'move', columnId: 'col:2', placement: 'top' }] },
        { name: 'Erledigt-Spalte hakt ab', trigger: { type: 'moved', columnId: 'col:4' }, actions: [{ type: 'complete' }] },
      ],
      cards: [
        c(0, 'Mitgliederumfrage digital', [4]),
        c(0, 'Neues Logo prüfen', [4]),
        c(1, 'Förderantrag Stiftung', [0], { dueInDays: 14, checklist: ['Ziel klären', 'Umsetzen', 'Prüfen lassen'] }),
        c(1, 'Raumplanung Herbst', [3]),
        c(2, 'Jahresbericht schreiben', [0], { checklist: ['Gliederung', 'Texte', 'Fotos', 'Layout'] }),
        c(1, 'Meilenstein: Jahresbericht gedruckt', [1], { dueInDays: 30, after: [4] }),
        c(1, 'Risiko: Druckerei ausgebucht', [2], { dueInDays: 10 }),
        c(3, 'Datenschutzhinweise aktualisiert', []),
        c(4, 'Kick-off durchgeführt', []),
      ],
    },
  },
  {
    id: 'builtin:okr',
    name: 'Ziele & Quartalsplanung',
    category: 'projekte',
    summary: 'Quartalsziele, Wochenplanung und messbare Ergebnisse (OKR)',
    description:
      'Verbindet große Ziele mit der Arbeit dieser Woche. Ziele stehen als eigene Spalte, Vorhaben wandern über „Diese Woche“ nach „Erreicht“. Ein Feld für den Fortschritt in Prozent zeigt, wie weit ihr seid.',
    tags: ['Quartal', 'Fortschritt in %', 'Wochenfokus'],
    color: '#0c8599',
    data: {
      columns: cols('Ziele', 'Vorhaben', { title: 'Diese Woche', wipLimit: 6 }, 'In Arbeit', 'Erreicht'),
      labels: [
        { name: 'Ziel', color: 'purple' },
        { name: 'Q1', color: 'sky' },
        { name: 'Q2', color: 'lime' },
        { name: 'Gefährdet', color: 'red' },
      ],
      fields: [{ name: 'Fortschritt (%)', type: 'number', showOnCard: true }],
      automations: [done(4)],
      cards: [
        c(0, 'Ziel: 200 neue Teilnehmende gewinnen', [0, 1]),
        c(0, 'Ziel: Abläufe digital und papierlos', [0, 1]),
        c(1, 'Anmeldung online statt per Fax', [1]),
        c(1, 'Kooperationsschulen ansprechen', [1]),
        c(2, 'Infoabend vorbereiten', [1], { dueInDays: 6 }),
        c(3, 'Formulare digitalisieren', [1, 3], { checklist: ['Anmeldung', 'Einverständnis', 'Abrechnung'] }),
        c(4, 'Neue Website online', [1]),
      ],
    },
  },
  {
    id: 'builtin:meeting',
    name: 'Besprechungen & Beschlüsse',
    category: 'projekte',
    summary: 'Themen sammeln, besprechen, beschließen und umsetzen',
    description:
      'Damit aus Besprechungen Ergebnisse werden: Alle sammeln Themen vorab, in der Sitzung wandern sie nach „Beschlossen“, und jede Umsetzung bekommt eine zuständige Person mit Frist. Eine Kartenvorlage hält Beschluss und Begründung fest.',
    tags: ['Tagesordnung', 'Beschlüsse', 'Kartenvorlage'],
    color: '#1971c2',
    data: {
      columns: cols('Themen', 'Nächste Sitzung', 'Beschlossen', { title: 'Umsetzung', entryRule: 'Zuständig und Frist stehen fest' }, 'Erledigt'),
      labels: [
        { name: 'Info', color: 'blue' },
        { name: 'Entscheidung', color: 'orange' },
        { name: 'Vertraulich', color: 'red' },
      ],
      cardTemplates: [
        {
          name: 'Beschluss',
          title: 'Beschluss: ',
          description: '<p><strong>Beschluss:</strong> </p><p><strong>Begründung:</strong> </p><p><strong>Abstimmung:</strong> </p>',
        },
      ],
      automations: [done(4), { name: 'Umsetzung: Frist 14 Tage', trigger: { type: 'moved', columnId: 'col:3' }, actions: [{ type: 'setDue', days: 14 }] }],
      cards: [
        c(0, 'Neue Öffnungszeiten', [1]),
        c(0, 'Budget 2027', [1, 2]),
        c(1, 'Bericht aus der Arbeitsgruppe', [0]),
        c(1, 'Termin Sommerfest', [1]),
        c(2, 'Beschluss: Fahrradständer anschaffen', [1]),
        c(3, 'Angebote für Fahrradständer einholen', [], { dueInDays: 10 }),
        c(4, 'Protokoll Oktober verschickt', [0]),
      ],
    },
  },
  {
    id: 'builtin:website',
    name: 'Website-Projekt',
    category: 'projekte',
    summary: 'Konzept, Gestaltung, Umsetzung, Test und Livegang',
    description:
      'Für den Relaunch oder eine neue Website: Seiten und Aufgaben laufen vom Konzept bis „Live“. Vor dem Livegang sorgt eine Testspalte mit Regel für Qualität, eine Kartenvorlage bringt die Checkliste je Seite mit.',
    tags: ['Seiten-Checkliste', 'Test vor Livegang'],
    color: '#e8590c',
    data: {
      columns: cols('Konzept', 'Gestaltung', { title: 'Umsetzung', wipLimit: 4 }, { title: 'Test', exitRule: 'Mobil, Desktop und Barrierefreiheit geprüft' }, 'Live'),
      labels: [
        { name: 'Design', color: 'purple' },
        { name: 'Inhalt', color: 'blue' },
        { name: 'Technik', color: 'green' },
        { name: 'SEO', color: 'yellow' },
      ],
      cardTemplates: [{ name: 'Seite', title: 'Seite: ', checklist: ['Text', 'Bilder mit Alternativtext', 'Mobil geprüft', 'Links geprüft'] }],
      automations: [done(4)],
      cards: [
        c(0, 'Sitemap abstimmen', [1]),
        c(0, 'Zielgruppen und Ziele', [1]),
        c(1, 'Startseite gestalten', [0], { checklist: ['Moodboard', 'Entwurf mobil', 'Entwurf Desktop'] }),
        c(1, 'Farbwelt und Schrift', [0]),
        c(2, 'Seite: Über uns', [1, 2], { checklist: ['Text', 'Bilder mit Alternativtext', 'Mobil geprüft', 'Links geprüft'] }),
        c(2, 'Kontaktformular', [2]),
        c(3, 'Ladezeit messen', [2, 3]),
        c(4, 'Domain umgestellt', [2]),
      ],
    },
  },

  /* ---------- Veranstaltungen ---------- */
  {
    id: 'builtin:event',
    version: 3,
    name: 'Veranstaltung planen',
    category: 'veranstaltungen',
    summary: 'Workshops, Kurse und Events mit Termin, Ort, Budget und Orga-Checkliste',
    description:
      'Von der Idee bis zur Nachbereitung: Jede Veranstaltung ist eine Karte mit Veranstaltungstag, Ort, Teilnehmendenzahl und Budget. Die Kartenvorlage bringt die bewährte Orga-Checkliste mit; nach der Durchführung erinnert KaBa an Bericht und Abrechnung.',
    tags: ['Termin & Ort', 'Budget', 'Orga-Checkliste', 'Nachbereitung'],
    color: '#2f9e44',
    data: {
      views: [view('Meine Aufgaben', { filter: { assignees: ['me'], completed: 'hide' }, sort: 'due' }), view('Kalender (Frist)', { view: 'calendar' }), view('Dringend', { filter: { labels: labelsOf(3) } })],
      overview: { widgets: [wg('deadlines', 12), wg('agenda', 6, { days: 30, rows: 10 }), wg('ready', 6, ROWS), wg('blocked', 6, ROWS)] },
      columns: cols(
        'Ideen',
        { title: 'In Planung', entryRule: 'Datum und Zielgruppe stehen' },
        'Orga läuft',
        'Durchgeführt',
        { title: 'Nachbereitet', entryRule: 'Bericht und Abrechnung erledigt' },
      ),
      labels: [
        { name: 'Schule', color: 'blue' },
        { name: 'Öffentlich', color: 'green' },
        { name: 'Förderung', color: 'purple' },
        { name: 'Dringend', color: 'red' },
      ],
      fields: [
        { name: 'Veranstaltungstag', type: 'date', showOnCard: true },
        { name: 'Ort', type: 'text', showOnCard: true },
        { name: 'Teilnehmende', type: 'number' },
        { name: 'Budget', type: 'currency' },
      ],
      cardTemplates: [
        {
          name: 'Veranstaltung',
          title: 'Neue Veranstaltung',
          checklist: [
            'Termin und Raum festlegen',
            'Referent*innen anfragen',
            'Material und Technik klären',
            'Einladung / Ankündigung verschicken',
            'Anmeldungen prüfen',
            'Teilnehmerliste und Fotos',
            'Nachbericht und Abrechnung',
          ],
          dueInDays: 28,
          assignCreator: true,
        },
      ],
      automations: [
        {
          name: 'Durchgeführt → Nachbereitung',
          trigger: { type: 'moved', columnId: 'col:3' },
          actions: [
            { type: 'setDue', days: 14 },
            { type: 'addChecklist', items: ['Nachbericht schreiben', 'Abrechnung einreichen'] },
          ],
        },
        { name: 'Nachbereitet hakt ab', trigger: { type: 'moved', columnId: 'col:4' }, actions: [{ type: 'complete' }] },
      ],
      cards: [
        c(0, 'Robotik-Workshop für Grundschulen', [0]),
        c(0, 'Tag der offenen Tür', [1]),
        c(1, 'Lehrkräftefortbildung Informatik', [0, 2], { dueInDays: 21 }),
        c(2, 'Ferienkurs Elektronik', [1], {
          checklist: ['Termin und Raum festlegen', 'Referent*innen anfragen', 'Material und Technik klären', 'Einladung / Ankündigung verschicken'],
        }),
        c(2, 'Infoabend Eltern', [1, 3], { dueInDays: 4 }),
        c(3, 'Mathe-Olympiade Regionalrunde', [0]),
        c(4, 'Sommerakademie 2026', [2]),
      ],
    },
  },
  {
    id: 'builtin:conference',
    version: 2,
    name: 'Tagung / Fachkonferenz',
    category: 'veranstaltungen',
    summary: 'Programm, Referierende, Räume und Teilnehmende im Griff',
    description:
      'Für Tagungen mit mehreren Programmpunkten: Jeder Vortrag oder Workshop läuft von der Anfrage bis zur Zusage, Organisationsaufgaben haben eigene Labels. Felder für Raum, Zeitfenster und Referent*in machen das Programm auf den Karten sichtbar. Vorschläge fürs Programm kommen auf Wunsch über das mitgelieferte Formular „Beitrag einreichen“ (Entwurf) – mit Kurzbeschreibung und Technikbedarf.',
    tags: ['Programm', 'Referierende', 'Räume & Zeiten', 'Formularentwurf'],
    color: '#4263eb',
    data: {
      columns: cols('Ideen fürs Programm', 'Angefragt', { title: 'Zugesagt', entryRule: 'Titel, Kurzbeschreibung und Technikbedarf liegen vor' }, 'Organisation', 'Fertig'),
      labels: [
        { name: 'Vortrag', color: 'blue' },
        { name: 'Workshop', color: 'green' },
        { name: 'Orga', color: 'gray' },
        { name: 'Catering', color: 'orange' },
      ],
      fields: [
        { name: 'Raum', type: 'text', showOnCard: true },
        { name: 'Zeitfenster', type: 'text', showOnCard: true },
        { name: 'Referent*in', type: 'text' },
      ],
      automations: [done(4), { name: 'Angefragt: Nachfassen in 7 Tagen', trigger: { type: 'moved', columnId: 'col:1' }, actions: [{ type: 'setDue', days: 7 }] }],
      forms: [{ template: 'beitrag', column: 0, title: 'Beitrag einreichen' }],
      cards: [
        c(0, 'Keynote: KI im Unterricht', [0]),
        c(1, 'Workshop: Experimente mit Alltagsmaterial', [1], { dueInDays: 7 }),
        c(1, 'Podium: Fachkräfte von morgen', [0]),
        c(2, 'Vortrag: MINT-Förderung im ländlichen Raum', [0]),
        c(3, 'Namensschilder drucken', [2], { dueInDays: 12 }),
        c(3, 'Mittagessen bestellen', [3], { checklist: ['Anzahl', 'Vegetarisch / vegan', 'Allergien'] }),
        c(4, 'Räume gebucht', [2]),
      ],
    },
  },
  {
    id: 'builtin:camp',
    name: 'Ferienprogramm / Camp',
    category: 'veranstaltungen',
    summary: 'Angebote, Betreuung, Plätze und Elterninfos für Ferienwochen',
    description:
      'Plant Ferienangebote für Kinder und Jugendliche: Jedes Angebot hat Plätze, Altersgruppe und Betreuung. Die Kartenvorlage denkt an Einverständniserklärungen, Notfallnummern und Elterninfo – ideal für Vereine, Schulen und Jugendarbeit.',
    tags: ['Plätze', 'Altersgruppe', 'Elterninfo'],
    color: '#f08c00',
    data: {
      columns: cols('Ideen', 'Ausgeschrieben', 'Ausgebucht', 'Läuft', 'Abgeschlossen'),
      labels: [
        { name: '6–9 Jahre', color: 'yellow' },
        { name: '10–13 Jahre', color: 'orange' },
        { name: 'Ab 14', color: 'red' },
        { name: 'Draußen', color: 'lime' },
      ],
      fields: [
        { name: 'Plätze', type: 'number', showOnCard: true },
        { name: 'Betreuung', type: 'person' },
        { name: 'Zeitraum', type: 'text', showOnCard: true },
      ],
      cardTemplates: [
        {
          name: 'Angebot',
          title: 'Neues Angebot',
          checklist: ['Ausschreibung', 'Betreuung geklärt', 'Einverständniserklärungen', 'Notfallnummern', 'Elterninfo verschickt', 'Material'],
        },
      ],
      automations: [done(4)],
      cards: [
        c(0, 'Bachforscher-Tag', [0, 3]),
        c(1, 'Programmieren mit Calliope', [1], { checklist: ['Ausschreibung', 'Betreuung geklärt', 'Einverständniserklärungen'] }),
        c(1, 'Kletterwald', [2, 3]),
        c(2, 'Erfinderwerkstatt', [1]),
        c(3, 'Zeltlager am See', [1, 3]),
        c(4, 'Osterferien: Raketenbau', [1]),
      ],
    },
  },
  {
    id: 'builtin:fair',
    name: 'Messeauftritt',
    category: 'veranstaltungen',
    summary: 'Stand, Material, Personal und Nachbereitung einer Messe',
    description:
      'Damit am Messetag alles da ist: Aufgaben laufen vom Plan über Bestellung bis zum Aufbau, ein Feld hält Kosten fest. Nach der Messe kümmert sich eine eigene Spalte um Kontakte und Auswertung.',
    tags: ['Kosten', 'Bestellungen', 'Kontakte nachfassen'],
    color: '#0b7285',
    data: {
      columns: cols('Planen', 'Bestellt / angefragt', 'Bereit', 'Messetag', 'Nachbereitung'),
      labels: [
        { name: 'Stand', color: 'blue' },
        { name: 'Material', color: 'purple' },
        { name: 'Personal', color: 'green' },
        { name: 'Kontakte', color: 'orange' },
      ],
      fields: [{ name: 'Kosten', type: 'currency', showOnCard: true }],
      automations: [{ name: 'Nachbereitung: Frist 7 Tage', trigger: { type: 'moved', columnId: 'col:4' }, actions: [{ type: 'setDue', days: 7 }] }],
      cards: [
        c(0, 'Standplan und Strom', [0]),
        c(0, 'Dienstplan Standpersonal', [2]),
        c(1, 'Roll-up drucken', [1], { dueInDays: 10 }),
        c(1, 'Give-aways bestellen', [1]),
        c(2, 'Präsentationsrechner', [0]),
        c(4, 'Kontakte ins CRM übertragen', [3], { dueInDays: 7 }),
      ],
    },
  },
  {
    id: 'builtin:party',
    name: 'Fest / Feier planen',
    category: 'veranstaltungen',
    summary: 'Sommerfest, Jubiläum oder Weihnachtsfeier ohne Stress',
    description:
      'Ein leichtes Board für Feste: Essen, Programm, Helfende und Einkäufe stehen als Labels bereit, eine Spalte „Am Tag“ sammelt alles, was erst kurz vorher passiert. Nach dem Fest bleibt Platz für Dank und Fotos.',
    tags: ['Helfende', 'Einkauf', 'Am Tag'],
    color: '#d6336c',
    data: {
      columns: cols('Ideen', 'Zu erledigen', 'Am Tag', 'Erledigt'),
      labels: [
        { name: 'Essen & Trinken', color: 'orange' },
        { name: 'Programm', color: 'purple' },
        { name: 'Helfende', color: 'green' },
        { name: 'Einkauf', color: 'yellow' },
      ],
      automations: [done(3)],
      cards: [
        c(0, 'Hüpfburg oder Spielstraße?', [1]),
        c(1, 'Grill und Kühlung organisieren', [0]),
        c(1, 'Helferliste rumschicken', [2], { dueInDays: 7 }),
        c(1, 'Einkaufsliste', [3], { checklist: ['Getränke', 'Grillgut', 'Salate', 'Becher und Servietten'] }),
        c(2, 'Musikanlage aufbauen', [1]),
        c(2, 'Kasse und Wechselgeld', [0]),
        c(3, 'Einladung verschickt', [1]),
      ],
    },
  },

  /* ---------- Bildung & Schule ---------- */
  {
    id: 'builtin:lesson',
    name: 'Unterrichtsplanung',
    category: 'bildung',
    summary: 'Stunden und Einheiten vorbereiten, durchführen und reflektieren',
    description:
      'Für Lehrkräfte und Kursleitungen: Jede Einheit wandert von der Idee über die Vorbereitung bis zur Reflexion. Felder für Klasse und Datum stehen auf der Karte; die Kartenvorlage strukturiert Ziel, Ablauf und Material.',
    tags: ['Klasse & Datum', 'Reflexion', 'Kartenvorlage'],
    color: '#1098ad',
    data: {
      columns: cols('Ideen', 'Vorbereiten', 'Bereit', 'Durchgeführt', 'Reflektiert'),
      labels: [
        { name: 'Mathe', color: 'blue' },
        { name: 'Physik', color: 'purple' },
        { name: 'Informatik', color: 'green' },
        { name: 'Projekt', color: 'orange' },
      ],
      fields: [
        { name: 'Klasse', type: 'text', showOnCard: true },
        { name: 'Datum', type: 'date', showOnCard: true },
      ],
      cardTemplates: [
        {
          name: 'Einheit',
          title: 'Einheit: ',
          description: '<p><strong>Lernziel:</strong> </p><p><strong>Ablauf:</strong> </p><p><strong>Material:</strong> </p>',
          checklist: ['Material vorbereiten', 'Arbeitsblatt', 'Differenzierung'],
        },
      ],
      automations: [done(4)],
      cards: [
        c(0, 'Escape-Room Bruchrechnung', [0]),
        c(1, 'Einheit: Stromkreise bauen', [1], { checklist: ['Material vorbereiten', 'Arbeitsblatt', 'Differenzierung'] }),
        c(1, 'Einheit: Algorithmen im Alltag', [2]),
        c(2, 'Lineare Funktionen – Stationenlernen', [0]),
        c(3, 'Projekttag Wetterstation', [3]),
        c(4, 'Einheit: Hebelgesetz', [1]),
      ],
    },
  },
  {
    id: 'builtin:competition',
    version: 2,
    name: 'Wettbewerb betreuen',
    category: 'bildung',
    summary: 'Teams, Anmeldungen, Arbeitsphase, Abgabe und Jury',
    description:
      'Für Schülerwettbewerbe und Challenges: Jedes Team ist eine Karte, die von der Anmeldung über Arbeitsphase und Abgabe bis zur Bewertung läuft. Felder für Schule, Altersklasse und Punkte machen den Stand sichtbar; vor der Abgabe erinnert KaBa an die Frist. Teams melden sich auf Wunsch über das mitgelieferte Formular „Team anmelden“ an (Entwurf, ohne Schülernamen).',
    tags: ['Teams', 'Altersklassen', 'Punkte', 'Jury', 'Formularentwurf'],
    color: '#ae3ec9',
    data: {
      columns: cols('Angemeldet', 'Arbeitsphase', { title: 'Abgegeben', entryRule: 'Projektbeschreibung und Video liegen vor' }, 'In Bewertung', 'Bewertet'),
      labels: [
        { name: 'Unterstufe', color: 'yellow' },
        { name: 'Mittelstufe', color: 'orange' },
        { name: 'Oberstufe', color: 'red' },
        { name: 'Nachfrage', color: 'sky' },
      ],
      fields: [
        { name: 'Schule', type: 'text', showOnCard: true },
        { name: 'Punkte', type: 'number', showOnCard: true },
        { name: 'Betreuende Lehrkraft', type: 'text' },
      ],
      automations: [{ name: 'Arbeitsphase: Abgabe in 30 Tagen', trigger: { type: 'moved', columnId: 'col:1' }, actions: [{ type: 'setDue', days: 30 }] }, done(4)],
      forms: [{ template: 'wettbewerb', column: 0, title: 'Team anmelden' }],
      cards: [
        c(0, 'Team „Solarflitzer“', [1]),
        c(0, 'Team „Bienenretter“', [0, 3]),
        c(1, 'Team „Smart Garden“', [2], { dueInDays: 30 }),
        c(1, 'Team „Plastikfrei“', [1]),
        c(2, 'Team „Wasserfilter 2.0“', [2]),
        c(3, 'Team „Leuchtende Pflanzen“', [1]),
        c(4, 'Team „RoboSort“', [2]),
      ],
    },
  },
  {
    id: 'builtin:projectweek',
    name: 'Projektwoche / AG',
    category: 'bildung',
    summary: 'Angebote, Leitungen, Räume und Ergebnisse einer Projektwoche',
    description:
      'Koordiniert Projektwochen und Arbeitsgemeinschaften: Angebote werden gesammelt, bekommen Leitung, Raum und Plätze und stellen am Ende ihre Ergebnisse vor. Eine Spalte für die Präsentation sorgt für einen sichtbaren Abschluss.',
    tags: ['Leitung & Raum', 'Plätze', 'Präsentation'],
    color: '#2b8a3e',
    data: {
      columns: cols('Vorschläge', 'Bestätigt', 'Läuft', 'Präsentation', 'Fertig'),
      labels: [
        { name: 'Naturwissenschaft', color: 'green' },
        { name: 'Technik', color: 'blue' },
        { name: 'Kunst', color: 'pink' },
        { name: 'Sport', color: 'orange' },
      ],
      fields: [
        { name: 'Leitung', type: 'text', showOnCard: true },
        { name: 'Raum', type: 'text', showOnCard: true },
        { name: 'Plätze', type: 'number' },
      ],
      automations: [done(4)],
      cards: [
        c(0, 'Insektenhotel bauen', [0]),
        c(0, 'Trickfilm-Werkstatt', [2]),
        c(1, 'Roboter programmieren', [1]),
        c(1, 'Schulgarten', [0]),
        c(2, 'Zirkus-AG', [3]),
        c(3, 'Schülerzeitung', [2]),
      ],
    },
  },
  {
    id: 'builtin:course',
    name: 'Kurs entwickeln',
    category: 'bildung',
    summary: 'Module eines Kurses oder Lernangebots planen und erstellen',
    description:
      'Für Online-Kurse, Fortbildungen oder Lernpfade: Module laufen vom Entwurf über Erstellung und Review bis zur Veröffentlichung. Die Kartenvorlage hält Lernziel, Dauer und Material fest, ein Review-Schritt sichert die Qualität.',
    tags: ['Module', 'Review', 'Lernziele'],
    color: '#5c7cfa',
    data: {
      columns: cols('Entwurf', 'In Erstellung', { title: 'Review', exitRule: 'Fachlich und didaktisch geprüft' }, 'Bereit', 'Veröffentlicht'),
      labels: [
        { name: 'Video', color: 'red' },
        { name: 'Text', color: 'blue' },
        { name: 'Übung', color: 'green' },
        { name: 'Quiz', color: 'purple' },
      ],
      fields: [{ name: 'Dauer (Min.)', type: 'number', showOnCard: true }],
      cardTemplates: [{ name: 'Modul', title: 'Modul: ', description: '<p><strong>Lernziel:</strong> </p><p><strong>Inhalte:</strong> </p>', checklist: ['Skript', 'Material', 'Übung', 'Review'] }],
      automations: [done(4)],
      cards: [
        c(0, 'Modul: Einführung', [1]),
        c(1, 'Modul: Erste Schritte', [0, 2], { checklist: ['Skript', 'Material', 'Übung', 'Review'] }),
        c(1, 'Modul: Praxisbeispiele', [0]),
        c(2, 'Abschlussquiz', [3]),
        c(4, 'Kursbeschreibung', [1]),
      ],
    },
  },
  {
    id: 'builtin:thesis',
    name: 'Abschlussarbeit',
    category: 'bildung',
    summary: 'Kapitel, Recherche und Abgabe einer Bachelor- oder Masterarbeit',
    description:
      'Teilt eine große Arbeit in überschaubare Schritte: Recherche, Kapitel schreiben, Korrektur, fertig. Die Kapitel stehen als Beispielkarten bereit; Labels zeigen, was noch Quellen oder Rückmeldung braucht.',
    tags: ['Kapitel', 'Korrektur', 'Persönlich'],
    color: '#495057',
    cardsDefault: true,
    data: {
      columns: cols('Recherche', 'Schreiben', 'Korrektur', 'Fertig'),
      labels: [
        { name: 'Quellen fehlen', color: 'yellow' },
        { name: 'Rückmeldung Betreuung', color: 'sky' },
        { name: 'Grafik', color: 'purple' },
      ],
      automations: [done(3)],
      cards: [
        c(0, 'Forschungsfrage schärfen', [1]),
        c(0, 'Literaturliste', [0]),
        c(1, 'Kapitel 1: Einleitung', []),
        c(1, 'Kapitel 2: Grundlagen', [0]),
        c(1, 'Kapitel 3: Methode', [2]),
        c(1, 'Kapitel 4: Ergebnisse', [2]),
        c(1, 'Kapitel 5: Diskussion und Fazit', []),
        c(2, 'Abstract', [1]),
      ],
    },
  },

  /* ---------- Marketing & Kommunikation ---------- */
  {
    id: 'builtin:editorial',
    version: 4,
    name: 'Redaktionsplan',
    category: 'marketing',
    summary: 'Beiträge von der Idee bis zur Veröffentlichung – Frist = Erscheinungstag',
    description:
      'Für Website, Social Media, Newsletter und Presse: Beiträge laufen von der Idee über Entwurf und Freigabe bis zur Veröffentlichung. Die Frist einer Karte ist der Tag der Veröffentlichung – so steht jeder Beitrag im Kalender. Das Feld „Entwurf fertig bis“ ist die interne Abgabe. KaBa veröffentlicht nichts selbst: „Veröffentlicht“ hält fest, dass ihr es getan habt. Mitglieder und Kolleg*innen schlagen Themen auf Wunsch über das mitgelieferte Formular „Thema vorschlagen“ vor (Entwurf).',
    tags: ['Kanäle', 'Freigabe', 'Kalender = Erscheinungstag', 'Formularentwurf'],
    color: '#c2255c',
    data: {
      views: [
        view('Redaktionskalender', { view: 'calendar' }),
        view('Nach Kanal', { lanes: 'label' }),
        view('Meine Beiträge', { filter: { assignees: ['me'], completed: 'hide' }, sort: 'due' }),
      ],
      columns: cols('Ideen', 'Entwurf', { title: 'Freigabe', exitRule: 'Freigegeben durch Verantwortliche' }, 'Geplant', 'Veröffentlicht'),
      labels: [
        { name: 'Website', color: 'blue' },
        { name: 'Instagram', color: 'purple' },
        { name: 'Newsletter', color: 'yellow' },
        { name: 'Presse', color: 'orange' },
      ],
      fields: [{ name: 'Entwurf fertig bis', type: 'date', showOnCard: true }],
      cardTemplates: [
        {
          name: 'Beitrag',
          title: 'Neuer Beitrag',
          description: '<p><strong>Frist</strong> = Tag der Veröffentlichung. <strong>Entwurf fertig bis</strong> = interne Abgabe für Text und Bild.</p><p><strong>Kernaussage:</strong> </p>',
          checklist: ['Text', 'Bild / Grafik', 'Freigabe', 'Planen'],
          assignCreator: true,
        },
      ],
      automations: [
        { name: 'Veröffentlicht hakt ab', trigger: { type: 'moved', columnId: 'col:4' }, actions: [{ type: 'complete' }] },
        {
          name: 'Zur Freigabe: Verantwortliche informieren',
          trigger: { type: 'moved', columnId: 'col:2' },
          actions: [{ type: 'notify', text: 'Ein Beitrag wartet auf Freigabe.', recipients: { owner: true, assignees: true, users: [], groups: [] } }],
        },
      ],
      forms: [{ template: 'themenvorschlag', column: 0, title: 'Thema vorschlagen' }],
      cards: [
        c(0, 'Blick hinter die Kulissen', [1]),
        c(0, 'Interview mit einer Ehrenamtlichen', [0, 2]),
        c(1, 'Rückblick Sommerakademie', [0], { dueInDays: 10, checklist: ['Text', 'Bild / Grafik', 'Freigabe', 'Planen'] }),
        c(2, 'Pressemitteilung Jubiläum', [3], { dueInDays: 5 }),
        c(3, 'Gewinnspiel Herbst', [1], { dueInDays: 3 }),
        c(4, 'Newsletter September', [2]),
      ],
    },
  },
  {
    id: 'builtin:campaign',
    name: 'Kampagne',
    category: 'marketing',
    summary: 'Botschaften, Maßnahmen und Kanäle einer Kampagne steuern',
    description:
      'Bündelt alle Maßnahmen einer Kampagne: von Strategie und Gestaltung über Produktion bis zum Ausspielen und Auswerten. Felder für Kanal und Budget halten den Überblick, eine Auswertungsspalte sichert das Lernen für das nächste Mal.',
    tags: ['Budget', 'Kanäle', 'Auswertung'],
    color: '#f76707',
    data: {
      columns: cols('Strategie', 'Gestaltung', 'Produktion', 'Läuft', 'Ausgewertet'),
      labels: [
        { name: 'Online', color: 'blue' },
        { name: 'Print', color: 'gray' },
        { name: 'Social', color: 'purple' },
        { name: 'Event', color: 'green' },
      ],
      fields: [{ name: 'Budget', type: 'currency', showOnCard: true }],
      automations: [{ name: 'Läuft: Auswertung in 30 Tagen', trigger: { type: 'moved', columnId: 'col:3' }, actions: [{ type: 'setDue', days: 30 }] }, done(4)],
      cards: [
        c(0, 'Zielgruppe und Kernbotschaft', []),
        c(1, 'Plakatmotiv', [1]),
        c(1, 'Social-Media-Kacheln', [2]),
        c(2, 'Anzeige Lokalzeitung', [1]),
        c(3, 'Instagram-Reihe „Wir sind dabei“', [2]),
        c(4, 'Infostand Wochenmarkt', [3]),
      ],
    },
  },
  {
    id: 'builtin:gotomarket',
    version: 2,
    name: 'Produktstart / Go-to-Market',
    category: 'marketing',
    summary: 'Vom Ziel bis zur Erfolgskontrolle – alle Termine ab dem Launchdatum',
    description:
      'Für einen Produkt- oder Angebotsstart: Beim Einrichten nennst du das Launchdatum, alle Beispielkarten bekommen ihre Frist relativ dazu. Abhängigkeiten verbinden Botschaft, Gestaltung, Landingpage, Kommunikation und Erfolgskontrolle – du siehst sofort, was den Start aufhält. KaBa versendet und veröffentlicht nichts selbst.',
    tags: ['Launchdatum', 'Abhängigkeiten', 'Erfolgskriterium', 'Launch-Checkliste'],
    color: '#e8590c',
    cardsDefault: true,
    data: {
      views: [view('Zeitplan bis zum Launch', { view: 'timeline' }), view('Kalender', { view: 'calendar' }), view('Was hält den Start auf?', { view: 'list', filter: { completed: 'hide' }, sort: 'due' })],
      overview: { widgets: [wg('deadlines', 12), wg('ready', 6, ROWS, 'Startbereit'), wg('blocked', 6, ROWS), wg('agenda', 12, { days: 30, rows: 10 }, 'Nächste 30 Tage')] },
      setup: { label: 'Launchdatum', help: 'Alle Termine des Plans rechnen ab diesem Tag (z. B. Landingpage zwei Wochen vorher).' },
      columns: cols('Strategie', 'Vorbereitung', 'Gestaltung', { title: 'Launch', exitRule: 'Launch-Checkliste vollständig' }, 'Erfolgskontrolle', 'Erledigt'),
      labels: [
        { name: 'Online', color: 'blue' },
        { name: 'Presse', color: 'orange' },
        { name: 'Social', color: 'purple' },
        { name: 'Vertrieb', color: 'green' },
      ],
      fields: [
        { name: 'Zielgruppe', type: 'text', showOnCard: true },
        { name: 'Botschaft', type: 'text' },
        { name: 'Kanal', type: 'select', options: opts(['web', 'Website', 'blue'], ['social', 'Social Media', 'purple'], ['mail', 'Newsletter', 'yellow'], ['presse', 'Presse', 'orange'], ['event', 'Veranstaltung', 'green']) },
        { name: 'Budget', type: 'currency', showOnCard: true },
        { name: 'Erfolgskriterium', type: 'text' },
      ],
      cardTemplates: [
        {
          name: 'Maßnahme',
          title: 'Neue Maßnahme',
          description: '<p><strong>Zielgruppe und Botschaft:</strong> </p><p><strong>Erfolgskriterium:</strong> Woran merken wir, dass es gewirkt hat?</p>',
          assignCreator: true,
        },
      ],
      automations: [
        done(5),
        {
          name: 'Erfolgskontrolle: Frist in 30 Tagen',
          trigger: { type: 'moved', columnId: 'col:4' },
          when: { op: 'all', items: [{ type: 'dates', which: 'due', op: 'missing' }] },
          actions: [{ type: 'setDue', days: 30 }],
        },
      ],
      cards: [
        c(0, 'Zielgruppe und Kernbotschaft festlegen', [], { dueFromSetup: -42 }),
        c(0, 'Budget und Erfolgskriterien', [], { dueFromSetup: -35 }),
        c(2, 'Kampagnenmotive gestalten', [2], { dueFromSetup: -21, after: [0] }),
        c(1, 'Landingpage', [0], { dueFromSetup: -14, after: [0, 2] }),
        c(1, 'Pressemitteilung', [1], { dueFromSetup: -10, after: [0] }),
        c(1, 'Newsletter zum Launch', [0], { dueFromSetup: -7, after: [2, 3] }),
        c(1, 'Social-Media-Plan', [2], { dueFromSetup: -7, after: [2] }),
        c(3, 'Launch', [], { dueFromSetup: 0, after: [3, 4, 5, 6], checklist: ['Landingpage online', 'Pressemitteilung verschickt', 'Newsletter verschickt', 'Social-Media-Beiträge geplant', 'Team informiert'] }),
        c(4, 'Erfolgskontrolle nach 30 Tagen', [3], { dueFromSetup: 30, after: [7], checklist: ['Zahlen gegen Erfolgskriterien', 'Was lief gut?', 'Was ändern wir?'] }),
      ],
    },
  },
  {
    id: 'builtin:newsletter',
    version: 3,
    name: 'Newsletter-Ausgaben',
    category: 'marketing',
    summary: 'Themen sammeln, Ausgaben planen – Frist = Versandtag, Versandstatus im Feld',
    description:
      'Themen werden gesammelt, einer Ausgabe zugeordnet, geschrieben und verschickt. Die Frist einer Ausgabe ist der Versandtag (so steht sie im Kalender), das Feld „Versand“ zeigt den Stand vom Entwurf bis „Verschickt“. KaBa verschickt selbst nichts – die Spalte „Verschickt“ hält fest, dass ihr verschickt habt. Themen für den Themenspeicher kommen auf Wunsch über das mitgelieferte Formular „Thema vorschlagen“ (Entwurf).',
    tags: ['Themenspeicher', 'Versand-Checkliste', 'Frist = Versandtag', 'Formularentwurf'],
    color: '#fab005',
    data: {
      columns: cols('Themenspeicher', 'Nächste Ausgabe', 'Geschrieben', 'Verschickt'),
      labels: [
        { name: 'Aufmacher', color: 'red' },
        { name: 'Termin', color: 'blue' },
        { name: 'Kurz notiert', color: 'gray' },
        { name: 'Ausgabe', color: 'yellow' },
      ],
      fields: [
        {
          name: 'Versand',
          type: 'select',
          showOnCard: true,
          options: opts(['entwurf', 'Entwurf', 'gray'], ['test', 'Testversand geprüft', 'yellow'], ['geplant', 'Eingeplant', 'blue'], ['verschickt', 'Verschickt', 'green']),
        },
      ],
      cardTemplates: [
        {
          name: 'Ausgabe',
          title: 'Newsletter ',
          labelIds: ['label:3'],
          description: '<p><strong>Frist</strong> = Versandtag. Den Stand trägst du im Feld „Versand“ ein.</p>',
          checklist: ['Themen festlegen', 'Texte', 'Bilder', 'Testversand', 'Versand'],
          custom: { 'field:0': 'entwurf' },
        },
      ],
      automations: [
        {
          name: 'Verschickt: Versand vermerken und abhaken',
          trigger: { type: 'moved', columnId: 'col:3' },
          actions: [{ type: 'setField', fieldId: 'field:0', value: 'verschickt' }, { type: 'complete' }],
        },
      ],
      forms: [{ template: 'themenvorschlag', column: 0, title: 'Thema vorschlagen' }],
      cards: [
        c(0, 'Neue Kursreihe ab Januar', [1]),
        c(0, 'Danke an die Helfenden', [2]),
        c(1, 'Aufmacher: Preisverleihung', [0]),
        c(1, 'Termine November', [1]),
        c(2, 'Newsletter November', [3], { dueInDays: 7, custom: { 'field:0': 'test' }, checklist: ['Themen festlegen', 'Texte', 'Bilder', 'Testversand', 'Versand'] }),
        c(3, 'Newsletter Oktober', [3], { custom: { 'field:0': 'verschickt' } }),
      ],
    },
  },
  {
    id: 'builtin:press',
    name: 'Presse & Medien',
    category: 'marketing',
    summary: 'Pressemitteilungen, Anfragen und Berichterstattung im Blick',
    description:
      'Für Pressearbeit: Mitteilungen laufen vom Entwurf über Abstimmung bis zum Versand, Medienanfragen bekommen eine Frist. Eine Spalte „Erschienen“ sammelt die Berichterstattung.',
    tags: ['Mitteilungen', 'Anfragen mit Frist', 'Pressespiegel'],
    color: '#343a40',
    data: {
      columns: cols('Themen', 'Entwurf', { title: 'Abstimmung', exitRule: 'Zitate freigegeben' }, 'Verschickt', 'Erschienen'),
      labels: [
        { name: 'Mitteilung', color: 'blue' },
        { name: 'Anfrage', color: 'orange' },
        { name: 'Interview', color: 'purple' },
      ],
      fields: [{ name: 'Medium', type: 'text', showOnCard: true }],
      automations: [{ name: 'Neue Anfrage: Antwort in 2 Tagen', trigger: { type: 'created', columnId: 'col:0' }, actions: [{ type: 'setDue', days: 2 }] }],
      cards: [
        c(0, 'Anfrage Lokalradio', [1], { dueInDays: 2 }),
        c(1, 'PM: Neue Förderung', [0]),
        c(2, 'Interview Regionalzeitung', [2]),
        c(3, 'PM: Preisverleihung', [0]),
        c(4, 'Artikel Stadtanzeiger', [0]),
      ],
    },
  },

  /* ---------- Design & Medien (0.67) ---------- */
  {
    id: 'builtin:graphic',
    version: 2,
    name: 'Grafik- und Designaufträge',
    category: 'design',
    summary: 'Von der Anfrage über Briefing und Prüfung bis zur Kundenfreigabe',
    description:
      'Für Flyer, Plakate, Social-Media-Grafiken und Präsentationen: Aufträge kommen als Anfrage (auf Wunsch über das mitgelieferte Formular), bekommen ein Briefing, werden gestaltet, intern geprüft und von der Auftraggeberin freigegeben. Sind Briefing-Checkliste und eine Datei da, markiert eine Regel die Karte als „Prüfbereit“ – die Freigabe selbst ist immer eine ausdrückliche Entscheidung im Feld „Freigabe“.',
    tags: ['Briefing', 'Interne Prüfung', 'Kundenfreigabe', 'Formularentwurf'],
    color: '#d6336c',
    data: {
      views: [
        view('Meine Aufträge', { filter: { assignees: ['me'], completed: 'hide' }, sort: 'due' }),
        view('Dringend', { filter: { labels: labelsOf(2), completed: 'hide' }, sort: 'due' }),
        view('Prüfbereit', { view: 'list', filter: { labels: labelsOf(4), completed: 'hide' } }),
      ],
      overview: { widgets: [wg('deadlines', 12), wg('tasks', 6, { rows: 10, sort: 'due' }, 'Ohne Zuständige', { assignees: ['none'] }), wg('blocked', 6, ROWS), wg('flow', 12)] },
      columns: cols(
        'Anfrage',
        { title: 'Briefing', exitRule: 'Ziel, Format, Inhalte und Termin sind geklärt' },
        { title: 'Gestaltung', wipLimit: 4 },
        { title: 'Interne Prüfung', exitRule: 'Eine zweite Person hat geprüft' },
        { title: 'Kundenfreigabe', exitRule: 'Freigabe ausdrücklich im Feld „Freigabe“ erteilt' },
        { title: 'Fertig', color: 'green' },
      ),
      labels: [
        { name: 'Druck', color: 'orange' },
        { name: 'Digital', color: 'blue' },
        { name: 'Dringend', color: 'red' },
        { name: 'Korrektur', color: 'yellow' },
        { name: 'Prüfbereit', color: 'green' },
      ],
      fields: [
        { name: 'Format / Abmessungen', type: 'text', showOnCard: true },
        { name: 'Medium', type: 'select', options: opts(['print', 'Print', 'orange'], ['web', 'Web', 'blue'], ['social', 'Social Media', 'purple'], ['praesentation', 'Präsentation', 'gray']) },
        { name: 'Auftraggeber', type: 'text' },
        { name: 'Freigabe durch', type: 'person' },
        { name: 'Freigabe', type: 'select', showOnCard: true, options: opts(['offen', 'Offen', 'gray'], ['freigegeben', 'Freigegeben', 'green'], ['aenderungen', 'Änderungen gewünscht', 'orange']) },
        { name: 'Ausgabelink', type: 'url' },
        { name: 'Wunschtermin', type: 'date' },
      ],
      cardTemplates: [
        {
          name: 'Gestaltungsauftrag',
          title: 'Neuer Auftrag',
          description:
            '<p><strong>Ziel und Zielgruppe:</strong> </p><p><strong>Inhalte (Texte, Logos, Bilder):</strong> </p><p><strong>Vorgaben (Farben, Schriften):</strong> </p><p><strong>Frist</strong> = verbindlicher Abgabetermin. Der Wunsch der Auftraggeberin steht im Feld „Wunschtermin“.</p>',
          checklist: ['Briefing vollständig', 'Bildrechte geklärt', 'Varianten erstellt', 'Finale Dateien abgelegt'],
          custom: { 'field:4': 'offen' },
        },
      ],
      automations: [
        {
          name: 'Checkliste und Datei da → Prüfbereit',
          trigger: { type: 'stateMet' },
          when: {
            op: 'all',
            items: [
              { type: 'column', mode: 'in', columnIds: ['col:2'] },
              { type: 'checklist', op: 'complete' },
              { type: 'files', kind: 'any', includeComments: false, min: 1 },
            ],
          },
          actions: [{ type: 'addLabel', labelId: 'label:4' }],
        },
        { name: 'Freigegeben → Fertig', trigger: fieldBecomes(4, 'freigegeben'), actions: [{ type: 'move', columnId: 'col:5', placement: 'top' }, { type: 'complete' }] },
        { name: 'Änderungen gewünscht → zurück in Gestaltung', trigger: fieldBecomes(4, 'aenderungen'), actions: [{ type: 'move', columnId: 'col:2', placement: 'top' }, { type: 'addLabel', labelId: 'label:3' }] },
      ],
      forms: [{ template: 'gestaltung', column: 0, title: 'Gestaltungsauftrag' }],
      cards: [
        c(0, 'Plakat Sommerfest', [0], { custom: { 'field:0': 'A3', 'field:1': 'print' } }),
        c(1, 'Social-Media-Kacheln Herbstprogramm', [1], { custom: { 'field:0': '1080 × 1350 px', 'field:1': 'social' }, checklist: ['Briefing vollständig', 'Bildrechte geklärt', 'Varianten erstellt', 'Finale Dateien abgelegt'] }),
        c(2, 'Flyer Kursangebot', [0, 2], { dueInDays: 7, custom: { 'field:0': 'DIN lang', 'field:1': 'print', 'field:4': 'offen' } }),
        c(4, 'Präsentation Jahresversammlung', [1], { custom: { 'field:1': 'praesentation', 'field:4': 'offen' } }),
      ],
    },
  },
  {
    id: 'builtin:print',
    version: 3,
    name: 'Druckproduktion',
    category: 'design',
    summary: 'Briefing, Layout, Korrektur, Druckfreigabe, Produktion – Frist = Liefertermin',
    description:
      'Für Flyer, Plakate und Broschüren, die in die Druckerei gehen: Jede Karte ist ein Druckauftrag mit Auflage, Endformat, Material und Druckerei. Die Frist ist der Liefertermin. Die Kartenvorlage bringt die Druckvorstufen-Checkliste mit (Beschnitt, Farbprofil, Schriften und Bilder, Freigabe, Lieferadresse); vor der Druckfreigabe bekommen die Verantwortlichen eine Nachricht. Aufträge kommen auf Wunsch über das mitgelieferte Formular „Druckauftrag“ (Entwurf) – Liefertermin wird zur Frist.',
    tags: ['Druckvorstufe', 'Druckfreigabe', 'Frist = Liefertermin', 'Formularentwurf'],
    color: '#495057',
    data: {
      views: [view('Liefertermine', { view: 'calendar' }), view('Eilaufträge', { filter: { labels: labelsOf(3), completed: 'hide' }, sort: 'due' })],
      columns: cols(
        'Briefing',
        'Layout',
        { title: 'Korrektur', exitRule: 'Alle Korrekturen eingearbeitet' },
        { title: 'Druckfreigabe', exitRule: 'Druckfreigabe schriftlich erteilt' },
        'Produktion',
        { title: 'Geliefert', color: 'green' },
      ),
      labels: [
        { name: 'Flyer', color: 'blue' },
        { name: 'Plakat', color: 'orange' },
        { name: 'Broschüre', color: 'purple' },
        { name: 'Eilauftrag', color: 'red' },
        { name: 'Nachdruck', color: 'gray' },
      ],
      fields: [
        { name: 'Auflage', type: 'number', showOnCard: true },
        { name: 'Endformat', type: 'text', showOnCard: true },
        { name: 'Material / Papier', type: 'text' },
        { name: 'Druckerei', type: 'text' },
      ],
      cardTemplates: [
        {
          name: 'Druckauftrag',
          title: 'Neuer Druckauftrag',
          description: '<p><strong>Frist</strong> = Liefertermin.</p><p><strong>Lieferadresse:</strong> </p><p><strong>Ansprechperson Druckerei:</strong> </p>',
          checklist: ['Beschnitt (3 mm) angelegt', 'Farbprofil geprüft (CMYK)', 'Schriften eingebettet, Bilder ≥ 300 dpi', 'Druckfreigabe erteilt', 'Lieferadresse bestätigt'],
          assignCreator: true,
        },
      ],
      automations: [
        {
          name: 'Druckfreigabe: Verantwortliche informieren',
          trigger: { type: 'moved', columnId: 'col:3' },
          actions: [{ type: 'notify', text: 'Ein Druckauftrag wartet auf die Druckfreigabe.', recipients: { owner: true, assignees: true, users: [], groups: [] } }],
        },
        done(5),
      ],
      forms: [{ template: 'druckauftrag', column: 0, title: 'Druckauftrag' }],
      cards: [
        c(0, 'Jahresbericht', [2], { custom: { 'field:0': 300, 'field:1': 'A4, 32 Seiten' } }),
        c(1, 'Flyer Herbstkurse', [0], { dueInDays: 14, custom: { 'field:0': 2000, 'field:1': 'DIN lang', 'field:2': '135 g Bilderdruck matt' } }),
        c(2, 'Plakat Tag der offenen Tür', [1, 3], { dueInDays: 6, custom: { 'field:0': 50, 'field:1': 'A2' } }),
        c(5, 'Visitenkarten Team', [4], { custom: { 'field:0': 250, 'field:1': '85 × 55 mm' } }),
      ],
    },
  },
  {
    id: 'builtin:video',
    name: 'Video- und Medienproduktion',
    category: 'design',
    summary: 'Idee, Konzept, Dreh, Schnitt, Freigabe – mit Format und Drehtermin',
    description:
      'Für Clips, Reels und Erklärvideos: Karten laufen von der Idee über Konzept und Produktion in den Schnitt und zur Freigabe. Felder halten Seitenverhältnis, Länge, Drehtermin, Schnittversion und die finale Adresse fest; Labels zeigen den Kanal. Die Frist ist der geplante Veröffentlichungstag.',
    tags: ['Seitenverhältnis', 'Drehtermin', 'Schnittversion', 'Freigabe'],
    color: '#7048e8',
    data: {
      columns: cols('Idee', 'Konzept', 'Produktion', 'Schnitt', { title: 'Freigabe', exitRule: 'Rechte geklärt, Untertitel geprüft' }, { title: 'Veröffentlicht', color: 'green' }),
      labels: [
        { name: 'Instagram', color: 'purple' },
        { name: 'YouTube', color: 'red' },
        { name: 'Website', color: 'blue' },
        { name: 'Intern', color: 'gray' },
      ],
      fields: [
        { name: 'Seitenverhältnis', type: 'select', showOnCard: true, options: opts(['16-9', '16:9', 'blue'], ['9-16', '9:16', 'purple'], ['1-1', '1:1', 'gray'], ['4-5', '4:5', 'yellow']) },
        { name: 'Länge', type: 'text' },
        { name: 'Drehtermin', type: 'date', showOnCard: true },
        { name: 'Schnittversion', type: 'number' },
        { name: 'Finale Adresse', type: 'url' },
      ],
      cardTemplates: [
        {
          name: 'Video',
          title: 'Neues Video',
          description: '<p><strong>Botschaft in einem Satz:</strong> </p><p><strong>Frist</strong> = geplanter Veröffentlichungstag, <strong>Drehtermin</strong> im Feld.</p>',
          checklist: ['Skript', 'Ton geprüft', 'Rechte (Musik, Personen)', 'Untertitel', 'Export in allen Formaten'],
          assignCreator: true,
        },
      ],
      automations: [
        {
          name: 'Zur Freigabe: Verantwortliche informieren',
          trigger: { type: 'moved', columnId: 'col:4' },
          actions: [{ type: 'notify', text: 'Ein Video wartet auf Freigabe.', recipients: { owner: true, assignees: true, users: [], groups: [] } }],
        },
        done(5),
      ],
      cards: [
        c(0, 'Reel: Ein Tag im Labor', [0], { custom: { 'field:0': '9-16' } }),
        c(1, 'Erklärvideo Anmeldung', [2], { custom: { 'field:0': '16-9', 'field:1': '90 s' } }),
        c(3, 'Rückblick Sommerakademie', [1], { dueInDays: 5, custom: { 'field:0': '16-9', 'field:3': 2 } }),
      ],
    },
  },
  {
    id: 'builtin:huddle',
    name: 'Designfeedback',
    category: 'design',
    summary: 'Entwürfe besprechen, Feedback sammeln und Entscheidungen festhalten',
    description:
      'Für Feedbackrunden zu Entwürfen: Jede Karte trennt Kontext, gewünschtes Feedback und Entscheidung. Wer entscheidet, steht im Feld „Entscheidung durch“; das Feld „Entscheidung“ hält fest, was gilt – „Überarbeiten“ schickt die Karte zurück, „Angenommen“ schließt sie ab.',
    tags: ['Feedbackrunde', 'Entscheidung', 'Kartenvorlage'],
    color: '#0ca678',
    data: {
      columns: cols('Zur Besprechung', 'Feedback sammeln', 'Entscheidung', 'Überarbeiten', { title: 'Abgeschlossen', color: 'green' }),
      labels: [
        { name: 'Grafik', color: 'blue' },
        { name: 'Web / App', color: 'purple' },
        { name: 'Text', color: 'yellow' },
        { name: 'Dringend', color: 'red' },
      ],
      fields: [
        { name: 'Designlink', type: 'url', showOnCard: true },
        { name: 'Fragestellung', type: 'text', showOnCard: true },
        { name: 'Entscheidung durch', type: 'person' },
        { name: 'Entscheidung', type: 'select', options: opts(['offen', 'Offen', 'gray'], ['angenommen', 'Angenommen', 'green'], ['ueberarbeiten', 'Überarbeiten', 'orange'], ['verworfen', 'Verworfen', 'red']) },
      ],
      cardTemplates: [
        {
          name: 'Feedbackrunde',
          title: 'Feedback: ',
          description:
            '<h3>Kontext</h3><p>Worum geht es, für wen, was ist schon entschieden?</p><h3>Gewünschtes Feedback</h3><p>Wozu genau brauchst du Rückmeldung – und wozu nicht?</p><h3>Entscheidung</h3><p>Was gilt jetzt? (Feld „Entscheidung“ setzen)</p>',
          custom: { 'field:3': 'offen' },
        },
      ],
      automations: [
        { name: 'Überarbeiten → zurückschicken', trigger: fieldBecomes(3, 'ueberarbeiten'), actions: [{ type: 'move', columnId: 'col:3', placement: 'top' }] },
        { name: 'Angenommen → abgeschlossen', trigger: fieldBecomes(3, 'angenommen'), actions: [{ type: 'move', columnId: 'col:4', placement: 'top' }, { type: 'complete' }] },
      ],
      cards: [
        c(0, 'Feedback: Startseite neu', [1], { custom: { 'field:1': 'Ist die Navigation verständlich?', 'field:3': 'offen' } }),
        c(1, 'Feedback: Logo-Varianten', [0], { custom: { 'field:1': 'Welche Variante passt zu uns?', 'field:3': 'offen' } }),
      ],
    },
  },
  {
    id: 'builtin:research',
    name: 'Research-Projekt',
    category: 'design',
    summary: 'Fragen, Durchführung, Auswertung – Erkenntnisse werden zu Maßnahmen',
    description:
      'Für Umfragen, Interviews und Nutzertests: Forschungsfragen laufen über Vorbereitung und Durchführung in die Auswertung; Erkenntnisse werden zu Maßnahmen, und jede Maßnahme bekommt eine zuständige Person. Rohdaten und Aufzeichnungen mit personenbezogenen Daten gehören nicht als Anhang ins Board – verlinke sie im Feld „Erkenntnislink“ auf einen geschützten Ablageort und markiere solche Karten mit „Sensibel“.',
    tags: ['Methode', 'Hypothese', 'Maßnahmen mit Zuständigkeit'],
    color: '#1c7ed6',
    data: {
      columns: cols('Fragen', 'Vorbereitung', 'Durchführung', 'Auswertung', 'Erkenntnisse', 'Maßnahmen', 'Erledigt'),
      labels: [
        { name: 'Interview', color: 'blue' },
        { name: 'Umfrage', color: 'green' },
        { name: 'Nutzertest', color: 'purple' },
        { name: 'Sensibel', color: 'red' },
      ],
      fields: [
        { name: 'Methode', type: 'select', showOnCard: true, options: opts(['interview', 'Interview', 'blue'], ['umfrage', 'Umfrage', 'green'], ['test', 'Nutzertest', 'purple'], ['desk', 'Desk Research', 'gray']) },
        { name: 'Zielgruppe', type: 'text' },
        { name: 'Hypothese', type: 'text' },
        { name: 'Erkenntnislink', type: 'url' },
      ],
      cardTemplates: [
        {
          name: 'Forschungsfrage',
          title: 'Frage: ',
          description: '<p><strong>Was wollen wir wissen?</strong> </p><p><strong>Hypothese:</strong> </p><p><strong>Wen fragen wir?</strong> </p>',
          checklist: ['Methode festlegen', 'Leitfaden / Fragebogen', 'Teilnehmende gewinnen', 'Einwilligung einholen', 'Durchführen', 'Auswerten'],
        },
        { name: 'Maßnahme', title: 'Maßnahme: ', description: '<p><strong>Aus welcher Erkenntnis?</strong> </p><p><strong>Fertig, wenn:</strong> </p>', assignCreator: true },
      ],
      automations: [
        {
          name: 'Maßnahme ohne Zuständige: mir zuweisen',
          trigger: { type: 'moved', columnId: 'col:5' },
          when: { op: 'all', items: [{ type: 'assignees', mode: 'none' }] },
          actions: [{ type: 'assign', userId: 'actor' }],
        },
        done(6),
      ],
      cards: [
        c(0, 'Frage: Warum brechen Anmeldungen ab?', [2], { custom: { 'field:0': 'test', 'field:2': 'Das Formular ist auf dem Handy zu lang.' } }),
        c(2, 'Interviews mit Lehrkräften', [0, 3], { custom: { 'field:0': 'interview' } }),
        c(4, 'Erkenntnis: Termine werden zu spät bekannt', []),
        c(5, 'Maßnahme: Terminübersicht im Frühjahr verschicken', []),
      ],
    },
  },

  /* ---------- Verein & Ehrenamt ---------- */
  {
    id: 'builtin:board',
    name: 'Vorstandsarbeit',
    category: 'verein',
    summary: 'Aufgaben, Beschlüsse und Fristen des Vorstands',
    description:
      'Hält die Vorstandsarbeit eines Vereins zusammen: Laufende Aufgaben, offene Beschlüsse und wiederkehrende Fristen wie Mitgliederversammlung, Steuererklärung oder Versicherung. Labels zeigen das Ressort.',
    tags: ['Ressorts', 'Fristen', 'Beschlüsse'],
    color: '#364fc7',
    data: {
      columns: cols('Offen', 'In Arbeit', 'Wartet auf Beschluss', 'Erledigt'),
      labels: [
        { name: 'Finanzen', color: 'green' },
        { name: 'Mitglieder', color: 'blue' },
        { name: 'Recht', color: 'red' },
        { name: 'Öffentlichkeit', color: 'purple' },
      ],
      automations: [done(3)],
      cards: [
        c(0, 'Einladung Mitgliederversammlung', [1], { dueInDays: 21 }),
        c(0, 'Versicherungen prüfen', [2]),
        c(1, 'Kassenprüfung vorbereiten', [0], { checklist: ['Belege sortieren', 'Kassenbuch', 'Termin Kassenprüfer'] }),
        c(1, 'Website aktualisieren', [3]),
        c(2, 'Beitragsordnung anpassen', [0, 2]),
        c(3, 'Freistellungsbescheid abgeheftet', [0]),
      ],
    },
  },
  {
    id: 'builtin:volunteers',
    version: 2,
    name: 'Ehrenamt & Helfende',
    category: 'verein',
    summary: 'Einsätze, Schichten und Ansprechpersonen für Ehrenamtliche',
    description:
      'Organisiert Helfende für Veranstaltungen und Dauerangebote: Einsätze werden ausgeschrieben, besetzt und bestätigt. Felder für Einsatztag und Anzahl der benötigten Personen machen Lücken sichtbar.',
    tags: ['Schichten', 'Besetzung', 'Bestätigung'],
    color: '#37b24d',
    data: {
      columns: cols('Gesucht', 'Teilweise besetzt', 'Besetzt', 'Erledigt'),
      labels: [
        { name: 'Aufbau', color: 'orange' },
        { name: 'Kasse', color: 'yellow' },
        { name: 'Betreuung', color: 'green' },
        { name: 'Abbau', color: 'gray' },
      ],
      fields: [
        { name: 'Einsatztag', type: 'date', showOnCard: true },
        { name: 'Benötigt', type: 'number', showOnCard: true },
      ],
      automations: [done(3)],
      cards: [
        c(0, 'Aufbau Sommerfest Freitag', [0]),
        c(0, 'Kuchenverkauf', [1]),
        c(1, 'Kinderbetreuung Samstag', [2]),
        c(2, 'Kasse Samstag Vormittag', [1]),
        c(2, 'Abbau Sonntag', [3]),
        c(3, 'Flyer verteilen', []),
      ],
    },
  },
  {
    id: 'builtin:funding',
    name: 'Förderanträge',
    category: 'verein',
    summary: 'Von der Recherche bis zur Abrechnung jedes Förderprogramms',
    description:
      'Behält alle Förderungen im Blick: Programme werden recherchiert, Anträge geschrieben und eingereicht, bewilligte Projekte bis zum Verwendungsnachweis begleitet. Felder für Fördersumme und Frist stehen auf der Karte.',
    tags: ['Fördersumme', 'Antragsfrist', 'Verwendungsnachweis'],
    color: '#7048e8',
    data: {
      columns: cols('Recherche', 'Antrag in Arbeit', 'Eingereicht', 'Bewilligt', { title: 'Abgerechnet', entryRule: 'Verwendungsnachweis eingereicht' }),
      labels: [
        { name: 'Stiftung', color: 'purple' },
        { name: 'Land', color: 'blue' },
        { name: 'Kommune', color: 'green' },
        { name: 'Abgelehnt', color: 'gray' },
      ],
      fields: [{ name: 'Fördersumme', type: 'currency', showOnCard: true }],
      cardTemplates: [
        {
          name: 'Antrag',
          title: 'Antrag: ',
          checklist: ['Förderrichtlinie lesen', 'Projektbeschreibung', 'Finanzplan', 'Unterschriften', 'Einreichen'],
        },
      ],
      automations: [{ name: 'Bewilligt: Nachweis-Erinnerung', trigger: { type: 'moved', columnId: 'col:3' }, actions: [{ type: 'addChecklist', items: ['Mittelabruf', 'Belege sammeln', 'Verwendungsnachweis'] }] }],
      cards: [
        c(0, 'Programm „Digital lernen“ prüfen', [1]),
        c(1, 'Antrag: Werkstattausstattung', [0], { dueInDays: 18, checklist: ['Förderrichtlinie lesen', 'Projektbeschreibung', 'Finanzplan'] }),
        c(2, 'Antrag: Ferienakademie', [2]),
        c(3, 'Antrag: Lehrkräftefortbildung', [1], { checklist: ['Mittelabruf', 'Belege sammeln', 'Verwendungsnachweis'] }),
        c(4, 'Antrag: Robotik-Sets 2025', [0]),
      ],
    },
  },
  {
    id: 'builtin:members',
    version: 2,
    name: 'Mitgliederbetreuung',
    category: 'verein',
    summary: 'Neue Mitglieder aufnehmen, begrüßen und einbinden',
    description:
      'Damit sich neue Mitglieder willkommen fühlen: Jede Aufnahme läuft vom Antrag über die Begrüßung bis zur ersten Beteiligung. Die Kartenvorlage denkt an Beitragseinzug, Willkommenspaket und Patenschaft. Anträge kommen auf Wunsch über das mitgelieferte Formular „Mitglied werden“ (Entwurf, ohne Bankdaten).',
    tags: ['Aufnahme', 'Willkommen', 'Kartenvorlage', 'Formularentwurf'],
    color: '#20c997',
    data: {
      columns: cols('Antrag eingegangen', 'Aufgenommen', 'Begrüßt', 'Aktiv dabei'),
      labels: [
        { name: 'Familie', color: 'green' },
        { name: 'Jugend', color: 'yellow' },
        { name: 'Förderer', color: 'purple' },
      ],
      cardTemplates: [{ name: 'Neues Mitglied', title: 'Neues Mitglied: ', checklist: ['Daten erfasst', 'Beitrag eingerichtet', 'Willkommensmail', 'Pate / Patin', 'Erstes Treffen'] }],
      automations: [done(3)],
      forms: [{ template: 'mitgliedsantrag', column: 0, title: 'Mitglied werden' }],
      cards: [
        c(0, 'Neues Mitglied: Familie Berger', [0]),
        c(1, 'Neues Mitglied: Jonas K.', [1], { checklist: ['Daten erfasst', 'Beitrag eingerichtet', 'Willkommensmail'] }),
        c(2, 'Neues Mitglied: Firma Holzbau', [2]),
        c(3, 'Neues Mitglied: Anna S.', []),
      ],
    },
  },

  /* ---------- Finanzen & Verwaltung (0.83, templateCatalogFinanz.ts) ---------- */
  ...FINANCE_TEMPLATES,

  /* ---------- Personal & Organisation ---------- */
  {
    id: 'builtin:onboarding',
    name: 'Onboarding',
    category: 'personal',
    summary: 'Neue Kolleg*innen gut ankommen lassen – vor, am und nach Tag 1',
    description:
      'Eine Checkliste, die mitdenkt: Vor dem ersten Tag, am ersten Tag, in der ersten Woche und nach dem ersten Monat. Die Beispielkarten enthalten die üblichen Schritte und werden standardmäßig mit angelegt – einfach anpassen.',
    tags: ['Checkliste', 'Erster Tag', 'Ein Board je Person'],
    color: '#0ca678',
    cardsDefault: true,
    data: {
      columns: cols('Vor dem ersten Tag', 'Erster Tag', 'Erste Woche', 'Erster Monat', 'Erledigt'),
      labels: [
        { name: 'IT', color: 'blue' },
        { name: 'Verwaltung', color: 'gray' },
        { name: 'Team', color: 'green' },
      ],
      automations: [done(4)],
      cards: [
        c(0, 'Arbeitsplatz und Schlüssel', [1]),
        c(0, 'Konten anlegen (E-Mail, KaBa, Ablage)', [0]),
        c(0, 'Begrüßungsmail ans Team', [2]),
        c(1, 'Rundgang und Vorstellung', [2]),
        c(1, 'Laptop und Telefon einrichten', [0]),
        c(2, 'Einarbeitungsplan besprechen', [2]),
        c(2, 'Datenschutz- und Sicherheitsunterweisung', [1]),
        c(3, 'Feedbackgespräch nach 4 Wochen', [2]),
      ],
    },
  },
  {
    id: 'builtin:recruiting',
    name: 'Bewerbungen',
    category: 'personal',
    summary: 'Bewerbungen sichten, Gespräche führen, entscheiden',
    description:
      'Ein ruhiger Überblick über ein Bewerbungsverfahren: Jede Bewerbung läuft von „Eingegangen“ über Gespräch bis zur Zusage oder Absage. Neue Bewerbungen bekommen automatisch eine Frist für die Rückmeldung. Tipp: Gebt das Board nur direkt an Personen frei.',
    tags: ['Rückmeldefrist', 'Gespräch', 'Vertraulich'],
    color: '#e64980',
    data: {
      columns: cols('Eingegangen', 'In Prüfung', 'Gespräch', 'Zusage', 'Absage'),
      labels: [
        { name: 'Vollzeit', color: 'blue' },
        { name: 'Teilzeit', color: 'green' },
        { name: 'Praktikum', color: 'yellow' },
        { name: 'Engere Wahl', color: 'purple' },
      ],
      fields: [{ name: 'Gesprächstermin', type: 'date', showOnCard: true }],
      automations: [{ name: 'Eingang: Rückmeldung in 5 Tagen', trigger: { type: 'created', columnId: 'col:0' }, actions: [{ type: 'setDue', days: 5 }] }],
      cards: [
        c(0, 'Bewerbung Projektreferent*in (M. L.)', [0], { dueInDays: 5 }),
        c(1, 'Bewerbung Projektreferent*in (S. A.)', [0, 3]),
        c(1, 'Bewerbung Praktikum (T. R.)', [2]),
        c(2, 'Bewerbung Verwaltung (K. M.)', [1, 3]),
        c(3, 'Bewerbung Werkstudent*in (F. N.)', [1]),
      ],
    },
  },
  {
    id: 'builtin:office',
    name: 'Büro & Verwaltung',
    category: 'personal',
    summary: 'Laufende und wiederkehrende Aufgaben im Büro',
    description:
      'Für Geschäftsstelle und Verwaltung: Laufende Aufgaben, Wartendes und Wiederkehrendes wie Monatsabschluss oder Post. Wiederkehrende Karten legen nach dem Erledigen von selbst die nächste an.',
    tags: ['Wiederkehrend', 'Wartet auf', 'Verwaltung'],
    color: '#868e96',
    data: {
      columns: cols('Eingang', 'Diese Woche', 'Wartet auf andere', 'Erledigt'),
      labels: [
        { name: 'Buchhaltung', color: 'green' },
        { name: 'Personal', color: 'blue' },
        { name: 'Einkauf', color: 'yellow' },
        { name: 'Dringend', color: 'red' },
      ],
      automations: [done(3)],
      cards: [
        c(0, 'Druckerpapier bestellen', [2]),
        c(1, 'Monatsabschluss', [0], { dueInDays: 3, checklist: ['Belege', 'Kontoabgleich', 'Ablage'], recurrence: { freq: 'monthly', interval: 1, basis: 'due' } }),
        c(1, 'Urlaubsplanung Dezember', [1]),
        c(2, 'Rückmeldung Steuerbüro', [0]),
        c(2, 'Angebot Reinigung', [2]),
        c(3, 'Reisekosten abgerechnet', [0]),
      ],
    },
  },

  /* ---------- Software & IT ---------- */
  {
    id: 'builtin:sprint',
    version: 2,
    name: 'Sprint / Scrum',
    category: 'it',
    summary: 'Backlog, Sprint, Review – mit Schätzung und Definition of Done',
    description:
      'Für Entwicklungsteams, die in Sprints arbeiten: Backlog, Sprint-Backlog, In Arbeit mit Begrenzung, Review mit Definition of Done. Ein Feld für Story Points und Labels für Feature, Fehler und Technik helfen bei der Planung.',
    tags: ['Story Points', 'Definition of Done', 'WIP-Limit'],
    color: '#1c7ed6',
    data: {
      views: [view('Meine Aufgaben', { filter: { assignees: ['me'], completed: 'hide' }, sort: 'due' }), view('Fehler', { filter: { labels: labelsOf(1), completed: 'hide' }, sort: 'priority' })],
      overview: { widgets: [wg('tasks', 6, { rows: 10, sort: 'priority' }), wg('blocked', 6, ROWS), wg('flow', 12), wg('activity', 6, ROWS)] },
      columns: cols('Backlog', 'Sprint', { title: 'In Arbeit', wipLimit: 4 }, { title: 'Review', exitRule: 'Definition of Done erfüllt' }, 'Fertig'),
      labels: [
        { name: 'Feature', color: 'green' },
        { name: 'Fehler', color: 'red' },
        { name: 'Technik', color: 'gray' },
        { name: 'UX', color: 'purple' },
      ],
      fields: [{ name: 'Story Points', type: 'number', showOnCard: true }],
      automations: [meOn(2), done(4)],
      cards: [
        c(0, 'Export als CSV', [0]),
        c(0, 'Dunkler Modus für Einstellungen', [3]),
        c(1, 'Anmeldung mit Passkey', [0]),
        c(1, 'Fehler: Datum springt bei Zeitumstellung', [1]),
        c(2, 'Suche beschleunigen', [2]),
        c(3, 'Neues Onboarding', [0, 3]),
        c(4, 'Abhängigkeiten aktualisiert', [2]),
      ],
    },
  },
  {
    id: 'builtin:bugs',
    version: 2,
    name: 'Fehler & Wünsche',
    category: 'it',
    summary: 'Rückmeldungen sammeln, bewerten, beheben und ausliefern',
    description:
      'Ein Eingangskorb für Fehlermeldungen und Ideen: Alles wird zuerst bewertet, dann priorisiert umgesetzt und mit der nächsten Version ausgeliefert. Überfällige Fehler werden automatisch als dringend markiert. Fehler und Ideen lassen sich über das mitgelieferte Formular „Rückmeldung geben“ einreichen (Entwurf).',
    tags: ['Triage', 'Priorität', 'Überfällig → dringend', 'Formularentwurf'],
    color: '#c92a2a',
    data: {
      columns: cols('Neu', 'Bewertet', 'In Arbeit', 'Im Test', 'Ausgeliefert'),
      labels: [
        { name: 'Fehler', color: 'red' },
        { name: 'Wunsch', color: 'sky' },
        { name: 'Dringend', color: 'orange' },
        { name: 'Doppelt', color: 'gray' },
      ],
      fields: [{ name: 'Version', type: 'text', showOnCard: true }],
      automations: [
        { name: 'Überfällig → dringend', trigger: { type: 'overdue' }, actions: [{ type: 'addLabel', labelId: 'label:2' }] },
        meOn(2),
        done(4),
      ],
      forms: [{ template: 'rueckmeldung', column: 0, title: 'Rückmeldung geben' }],
      cards: [
        c(0, 'Knopf am Handy zu klein', [0]),
        c(0, 'Wunsch: Vorlagen teilen', [1]),
        c(1, 'Export bricht bei Umlauten ab', [0], { priority: 3 }),
        c(2, 'Benachrichtigung doppelt', [0, 2]),
        c(3, 'Wunsch: Filter speichern', [1]),
        c(4, 'Absturz beim Hochladen großer Bilder', [0]),
      ],
    },
  },
  {
    id: 'builtin:itsupport',
    version: 2,
    name: 'IT-Support',
    category: 'it',
    summary: 'Tickets für Geräte, Konten und Software im Haus',
    description:
      'Für die interne IT: Tickets kommen herein, werden bearbeitet und warten wenn nötig auf Rückmeldung oder Teile. Neue Tickets bekommen eine Frist, und wer ein Ticket anfasst, wird zuständig. Mitarbeitende melden Probleme über das mitgelieferte Formular „IT-Problem melden“ (Entwurf).',
    tags: ['Tickets', 'Frist', 'Wartet auf Teile', 'Formularentwurf'],
    color: '#3bc9db',
    data: {
      columns: cols('Neu', { title: 'In Bearbeitung', wipLimit: 5 }, 'Wartet', 'Gelöst'),
      labels: [
        { name: 'Hardware', color: 'gray' },
        { name: 'Konto', color: 'blue' },
        { name: 'Software', color: 'green' },
        { name: 'Dringend', color: 'red' },
      ],
      automations: [
        { name: 'Neues Ticket: Frist 2 Tage', trigger: { type: 'created', columnId: 'col:0' }, actions: [{ type: 'setDue', days: 2 }] },
        meOn(1, 'Bearbeitung: mir zuweisen'),
        done(3),
      ],
      forms: [{ template: 'itticket', column: 0, title: 'IT-Problem melden' }],
      cards: [
        c(0, 'Drucker im 2. OG druckt nicht', [0], { dueInDays: 2 }),
        c(1, 'Neue Mitarbeiterin: Konten', [1]),
        c(1, 'Laptop sehr langsam', [0, 2]),
        c(2, 'Ersatzakku bestellt', [0]),
        c(3, 'Passwort zurückgesetzt', [1]),
      ],
    },
  },

  /* ---------- Anfragen & Service ---------- */
  {
    id: 'builtin:requests',
    version: 2,
    name: 'Anfragen & Support',
    category: 'service',
    summary: 'Eingang, Bearbeitung, Warten auf Rückmeldung – mit Erinnerung bei Frist',
    description:
      'Für alles, was von außen kommt: Anfragen per Telefon oder E-Mail landen im Eingang, bekommen automatisch eine Frist und wandern bis zur Erledigung. Wartet ihr auf Rückmeldung, setzt KaBa eine Nachfassfrist; Überfälliges wird als dringend markiert. Anfragen von außen kommen auf Wunsch über das mitgelieferte Formular „Anfrage“ (Entwurf).',
    tags: ['5 Automationen', 'WIP-Limit', 'Nachfassen', 'Formularentwurf'],
    color: '#1864ab',
    data: {
      columns: cols('Eingang', { title: 'In Bearbeitung', wipLimit: 6 }, 'Wartet auf Rückmeldung', 'Erledigt'),
      labels: [
        { name: 'Telefon', color: 'blue' },
        { name: 'E-Mail', color: 'green' },
        { name: 'Dringend', color: 'red' },
      ],
      automations: [
        { name: 'Neue Anfrage: Frist 3 Tage', trigger: { type: 'created', columnId: 'col:0' }, actions: [{ type: 'setDue', days: 3 }] },
        { name: 'Bearbeitung: mir zuweisen', trigger: { type: 'moved', columnId: 'col:1' }, actions: [{ type: 'assign', userId: 'actor' }] },
        { name: 'Wartet: Nachfassen in 5 Tagen', trigger: { type: 'moved', columnId: 'col:2' }, actions: [{ type: 'setDue', days: 5 }] },
        {
          name: 'Überfällig → dringend',
          trigger: { type: 'overdue' },
          actions: [
            { type: 'addLabel', labelId: 'label:2' },
            { type: 'notify', text: 'Frist überschritten.' },
          ],
        },
        { name: 'Erledigt hakt ab', trigger: { type: 'moved', columnId: 'col:3' }, actions: [{ type: 'complete' }] },
      ],
      forms: [{ template: 'anfrage', column: 0, title: 'Anfrage' }],
      cards: [
        c(0, 'Anfrage Schulkooperation', [1], { dueInDays: 3 }),
        c(0, 'Rückruf Frau Weber', [0], { dueInDays: 1 }),
        c(1, 'Fehlende Teilnahmebescheinigung', [1]),
        c(2, 'Angebot für Gruppenbuchung', [1], { dueInDays: 5 }),
        c(3, 'Adressänderung', [0]),
      ],
    },
  },
  {
    id: 'builtin:orders',
    version: 2,
    name: 'Bestellungen & Aufträge',
    category: 'service',
    summary: 'Von der Bestellung über Fertigung bis zur Auslieferung',
    description:
      'Für Werkstatt, Shop oder Druckerei: Jede Bestellung läuft von „Eingegangen“ über „In Fertigung“ bis „Ausgeliefert“. Felder für Kunde, Menge und Liefertermin stehen auf der Karte. Bestellungen kommen auf Wunsch über das mitgelieferte Formular „Bestellung aufgeben“ (Entwurf).',
    tags: ['Liefertermin', 'Menge', 'Kunde', 'Formularentwurf'],
    color: '#a61e4d',
    data: {
      columns: cols('Eingegangen', 'Bestätigt', { title: 'In Fertigung', wipLimit: 8 }, 'Versandbereit', 'Ausgeliefert'),
      labels: [
        { name: 'Express', color: 'red' },
        { name: 'Abholung', color: 'blue' },
        { name: 'Versand', color: 'green' },
      ],
      fields: [
        { name: 'Kunde', type: 'text', showOnCard: true },
        { name: 'Menge', type: 'number', showOnCard: true },
        { name: 'Liefertermin', type: 'date', showOnCard: true },
      ],
      automations: [done(4)],
      forms: [{ template: 'bestellung', column: 0, title: 'Bestellung aufgeben' }],
      cards: [
        c(0, 'Auftrag 1042 – Vereinsshirts', [2]),
        c(1, 'Auftrag 1039 – Schilder', [1]),
        c(2, 'Auftrag 1035 – Banner', [0]),
        c(3, 'Auftrag 1031 – Flyer', [2]),
        c(4, 'Auftrag 1027 – Visitenkarten', [1]),
      ],
    },
  },

  /* ---------- Vertrieb & Kunden ---------- */
  {
    id: 'builtin:sales',
    name: 'Vertriebspipeline',
    category: 'vertrieb',
    summary: 'Kontakte, Angebote und Abschlüsse mit Volumen und Wahrscheinlichkeit',
    description:
      'Von der ersten Kontaktaufnahme bis zum Abschluss: Jede Chance ist eine Karte mit Volumen und Wahrscheinlichkeit. Nach dem Angebot setzt KaBa eine Nachfassfrist, damit nichts liegen bleibt.',
    tags: ['Volumen', 'Wahrscheinlichkeit', 'Nachfassen'],
    color: '#087f5b',
    data: {
      columns: cols('Kontakt', 'Bedarf geklärt', 'Angebot', 'Verhandlung', 'Gewonnen', 'Verloren'),
      labels: [
        { name: 'Neukunde', color: 'green' },
        { name: 'Bestandskunde', color: 'blue' },
        { name: 'Groß', color: 'purple' },
      ],
      fields: [
        { name: 'Volumen', type: 'currency', showOnCard: true },
        { name: 'Wahrscheinlichkeit (%)', type: 'number', showOnCard: true },
      ],
      automations: [{ name: 'Angebot: Nachfassen in 7 Tagen', trigger: { type: 'moved', columnId: 'col:2' }, actions: [{ type: 'setDue', days: 7 }] }, done(4)],
      cards: [
        c(0, 'Stadtwerke – Schulung', [0]),
        c(1, 'Gymnasium Nord – Workshopreihe', [0, 2]),
        c(2, 'Landkreis – Ferienprogramm', [1], { dueInDays: 7 }),
        c(3, 'Firma Technik GmbH – Sponsoring', [2]),
        c(4, 'Realschule Süd – Projekttag', [1]),
        c(5, 'Kita-Verbund – Forscherkiste', [0]),
      ],
    },
  },
  {
    id: 'builtin:clients',
    version: 4,
    name: 'Kundenprojekte',
    category: 'vertrieb',
    summary: 'Aufträge vom Briefing bis zur Rechnung – Rechnung erst nach interner Freigabe',
    description:
      'Für Agenturen, Büros und Selbstständige: Jedes Projekt läuft von Briefing über Umsetzung und Abnahme bis zur Rechnung. Die Budgets stehen summiert im Spaltenkopf. Bevor eine Rechnung rausgeht, gibt jemand frei, der nicht selbst am Projekt gearbeitet hat; ändert sich danach das Budget, verfällt die Freigabe. Ein kleiner Kundenauftrag lässt sich in Briefing, Vorbereitung, Ergebnis und Übergabe aufteilen – Zusammenhänge zeigen, was worauf aufbaut, und offene Rollenplätze halten fest, wer ohne Kaba-Konto etwas beiträgt.',
    tags: ['Budget mit Summe', 'Abnahme', 'Rechnungsfreigabe', 'Zusammenhänge', 'Beteiligte ohne Konto'],
    adjust: ['Rollenplätze „Ansprechperson“ und „liefert“ beim Beispielauftrag einer Person zuordnen oder entfernen'],
    color: '#5c940d',
    data: {
      columns: cols(
        'Briefing',
        'In Umsetzung',
        { title: 'Abnahme', exitRule: 'Kunde hat abgenommen' },
        {
          title: 'Rechnung',
          entryGate: {
            when: { op: 'all', items: [{ type: 'field', fieldId: 'field:1', test: { op: 'approved' } }] },
            message: 'Die Rechnung braucht eine interne Freigabe (nicht durch Zuständige).',
            ownerOverride: true,
          },
        },
        'Abgeschlossen',
      ),
      labels: [
        { name: 'Design', color: 'purple' },
        { name: 'Web', color: 'blue' },
        { name: 'Beratung', color: 'green' },
      ],
      fields: [
        { name: 'Budget', pinned: true, type: 'currency', showOnCard: true, config: { aggregate: 'sum' } },
        { name: 'Rechnungsfreigabe', pinned: true, type: 'approval', showOnCard: true, config: { approval: { ...DEFAULT_APPROVAL, notCreator: false, notAssignee: true, resetFields: ['field:0'], resetFiles: false } } },
      ],
      automations: [
        { name: 'Abnahme: Verantwortliche um Rechnungsfreigabe bitten', trigger: { type: 'moved', columnId: 'col:2' }, actions: [{ type: 'notify', text: 'Ein Projekt ist in der Abnahme – bitte die Rechnung freigeben.', recipients: { owner: true, assignees: false, watchers: true, users: [], groups: [] } }] },
        { name: 'Freigegeben → Rechnung', trigger: { type: 'fieldChanged', fieldId: 'field:1', to: { op: 'approved' } }, when: { op: 'all', items: [{ type: 'column', mode: 'in', columnIds: ['col:2'] }] }, actions: [{ type: 'move', columnId: 'col:3', placement: 'bottom' }] },
        { name: 'Rechnung: Zahlungsziel 14 Tage', trigger: { type: 'moved', columnId: 'col:3' }, actions: [{ type: 'setDue', days: 14 }] },
        // 0.90 (K5): klare Zuständigkeit – wer mit der Umsetzung beginnt, ist zuständig (vorhandene Zuständige bleiben)
        meOn(1, 'Umsetzung beginnt: mir zuweisen'),
        done(4),
      ],
      // 0.90 (K5, Pilot „Kleiner Kundenauftrag“): Ziel, maßgebliches Briefing, Zusammenhänge, offene Rollenplätze
      context: {
        description:
          'Kundenaufträge vom Briefing bis zur Rechnung. Das Briefing einer Karte ist maßgeblich; Unterlagen an der Karte als „Briefing“ einordnen. In „Rechnung“ geht ein Auftrag erst mit interner Rechnungsfreigabe durch jemanden, der nicht zuständig war – ändert sich danach das Budget, verfällt die Freigabe.',
        outcome: 'Jeder Auftrag ist abgenommen, übergeben und abgerechnet',
        refs: [{ kind: 'card', card: 0, purpose: 'Beispiel: maßgebliches Briefing eines kleinen Auftrags' }],
      },
      cards: [
        c(0, 'Sonnenkorn – Briefing Herbstflyer', [0], {
          custom: { 'field:0': 800 },
          description: '<p><strong>Ziel:</strong> Herbstangebote bekannt machen.</p><p><strong>Format:</strong> Flyer DIN lang, 2-seitig.</p><p><strong>Termin:</strong> Druckdaten bis Monatsende.</p>',
          roles: [{ role: 'Ansprechperson', contribution: 'Inhalte und Abnahme auf Kundenseite' }],
          relations: [{ kind: 'basis', to: 1 }],
        }),
        c(1, 'Sonnenkorn – Texte und Fotos vorbereiten', [0], { roles: [{ role: 'liefert', contribution: 'Fotos der Backwaren und Preisliste' }], relations: [{ kind: 'delivers', to: 2 }] }),
        c(1, 'Sonnenkorn – Flyer gestalten', [0], { checklist: ['Entwurf', 'Korrektur Kunde', 'Druckdaten (PDF/X)'], relations: [{ kind: 'delivers', to: 3 }] }),
        c(2, 'Sonnenkorn – Druckdaten übergeben', [0], { roles: [{ role: 'Ansprechperson', contribution: 'Druckerei: nimmt die Druckdaten an' }] }),
        c(1, 'Stadtbibliothek – Website', [1], { custom: { 'field:0': 6500 } }),
        c(1, 'Sportverein – Logo', [0], { custom: { 'field:0': 1200 } }),
        c(2, 'Musikschule – Plakat', [0], { custom: { 'field:0': 450 } }),
        c(3, 'Weingut – Beratung', [2], { dueInDays: 14, custom: { 'field:0': 980 } }),
      ],
    },
  },

  /* ---------- Persönlich & Alltag ---------- */
  {
    id: 'builtin:week',
    name: 'Meine Woche',
    category: 'persoenlich',
    summary: 'Diese Woche, Heute, Erledigt – persönliche Aufgaben ordnen',
    description:
      'Ein schlankes persönliches Board: Alles Anstehende landet in „Diese Woche“, morgens zieht ihr die Aufgaben für heute herüber. Begrenzt „Heute“ auf wenige Karten – so bleibt der Tag machbar.',
    tags: ['Persönlich', 'Tagesfokus', 'WIP-Limit'],
    color: '#748ffc',
    data: {
      columns: cols('Irgendwann', 'Diese Woche', { title: 'Heute', wipLimit: 5 }, 'Erledigt'),
      labels: [
        { name: 'Arbeit', color: 'blue' },
        { name: 'Privat', color: 'green' },
        { name: 'Anrufen', color: 'yellow' },
      ],
      automations: [done(3)],
      cards: [
        c(0, 'Fotobuch Urlaub', [1]),
        c(1, 'Steuerunterlagen sortieren', [1]),
        c(1, 'Präsentation vorbereiten', [0]),
        c(2, 'Zahnarzt anrufen', [1, 2]),
        c(2, 'Bericht abschicken', [0]),
        c(3, 'Geschenk für Lena', [1]),
      ],
    },
  },
  {
    id: 'builtin:moving',
    name: 'Umzug',
    category: 'persoenlich',
    summary: 'Alles für den Umzug – von der Kündigung bis zur Ummeldung',
    description:
      'Die Umzugs-Checkliste als Board: Was Wochen vorher, in der letzten Woche, am Umzugstag und danach zu tun ist. Die üblichen Aufgaben sind schon da und werden standardmäßig mit angelegt.',
    tags: ['Checkliste', 'Zeitplan', 'Mit Beispielkarten'],
    color: '#e67700',
    cardsDefault: true,
    data: {
      columns: cols('6–4 Wochen vorher', '3–1 Wochen vorher', 'Umzugstag', 'Danach', 'Erledigt'),
      labels: [
        { name: 'Behörde', color: 'blue' },
        { name: 'Verträge', color: 'purple' },
        { name: 'Packen', color: 'orange' },
      ],
      automations: [done(4)],
      cards: [
        c(0, 'Alte Wohnung kündigen', [1]),
        c(0, 'Umzugsunternehmen oder Transporter', [1]),
        c(0, 'Urlaubstag beantragen', []),
        c(1, 'Kartons besorgen und packen', [2]),
        c(1, 'Halteverbot beantragen', [0]),
        c(1, 'Strom, Internet und Versicherungen ummelden', [1]),
        c(2, 'Zählerstände notieren', [1]),
        c(3, 'Wohnsitz ummelden (2 Wochen)', [0]),
        c(3, 'Nachsendeauftrag', [0]),
      ],
    },
  },
  {
    id: 'builtin:trip',
    name: 'Reise planen',
    category: 'persoenlich',
    summary: 'Ziele, Buchungen, Packliste und Programm einer Reise',
    description:
      'Für Urlaub, Klassenfahrt oder Teamreise: Ideen sammeln, Buchungen festhalten, Packliste abhaken und ein Programm für unterwegs. Ein Feld für Kosten hilft beim Budget.',
    tags: ['Buchungen', 'Packliste', 'Kosten'],
    color: '#15aabf',
    data: {
      columns: cols('Ideen', 'Zu buchen', 'Gebucht', 'Unterwegs', 'Erledigt'),
      labels: [
        { name: 'Unterkunft', color: 'blue' },
        { name: 'Anreise', color: 'green' },
        { name: 'Ausflug', color: 'orange' },
      ],
      fields: [{ name: 'Kosten', type: 'currency', showOnCard: true }],
      automations: [done(4)],
      cards: [
        c(0, 'Wanderung zum Wasserfall', [2]),
        c(1, 'Zugtickets', [1]),
        c(1, 'Mietwagen', [1]),
        c(2, 'Ferienwohnung', [0]),
        c(3, 'Packliste', [], { checklist: ['Ausweis', 'Ladegeräte', 'Regenjacke', 'Reiseapotheke', 'Snacks'] }),
      ],
    },
  },
  {
    id: 'builtin:household',
    name: 'Haushalt & Familie',
    category: 'persoenlich',
    summary: 'Wer macht was zu Hause – mit wiederkehrenden Aufgaben',
    description:
      'Verteilt Aufgaben fair in Familie oder WG: Diese Woche, In Arbeit, Erledigt. Labels für Bereiche und wiederkehrende Karten für Müll, Einkauf oder Rasenmähen machen den Alltag leichter.',
    tags: ['Familie & WG', 'Wiederkehrend'],
    color: '#40c057',
    data: {
      columns: cols('Diese Woche', 'In Arbeit', 'Erledigt'),
      labels: [
        { name: 'Küche', color: 'orange' },
        { name: 'Garten', color: 'green' },
        { name: 'Einkauf', color: 'yellow' },
        { name: 'Termine', color: 'blue' },
      ],
      automations: [done(2)],
      cards: [
        c(0, 'Müll rausbringen', [0], { recurrence: { freq: 'weekly', interval: 1, basis: 'completion' } }),
        c(0, 'Wocheneinkauf', [2], { checklist: ['Milch', 'Brot', 'Obst', 'Gemüse'], recurrence: { freq: 'weekly', interval: 1, basis: 'completion' } }),
        c(0, 'Elternabend Donnerstag', [3]),
        c(1, 'Rasen mähen', [1]),
        c(2, 'Fahrrad reparieren', []),
      ],
    },
  },
  TAX_RETURN_TEMPLATE,
];

/** „Leer starten“: drei Spalten, keine Labels, keine Regeln */
export const EMPTY_TEMPLATE: BuiltinTemplate = {
  id: 'builtin:empty',
  name: 'Leeres Board',
  category: 'projekte',
  summary: 'Drei Spalten, keine Regeln – alles Weitere legst du selbst fest',
  description: 'Ein ruhiger Start ohne Vorgaben: drei Spalten zum Umbenennen, keine Labels und keine Automationen.',
  tags: ['3 Spalten'],
  color: '#868e96',
  data: { columns: cols('Zu erledigen', 'In Arbeit', 'Erledigt'), labels: [] },
};
